import React, { useState, useEffect } from "react";
import axios from 'axios';
import { Link } from 'react-router-dom';
import { buildApiUrl } from './config';
import { getYearLabel as getYearLabelUtil } from './utils/yearUtils';
import AdminTimetableManager from './components/AdminTimetableManager';
import ParentsEveningSystem from './components/ParentsEveningSystem';
import EmergencyAlertsSimple from './components/EmergencyAlertsSimple';
import ClubBookingManager from './components/ClubBookingManager';
import UserManagement from './components/UserManagement';
import QuickLinksManager from './components/QuickLinksManager';
import ConsentFormsManager from './components/ConsentFormsManager';
import SchoolPoliciesManager from './components/SchoolPoliciesManager';
import ParentsManager from './components/ParentsManager';
import authService from './services/authService';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, CartesianGrid, XAxis, YAxis } from 'recharts';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import html2canvas from 'html2canvas';

// Global flag to prevent multiple simultaneous data fetches
let isGloballyFetching = false;
let globalDataCache = null;
let globalCacheTimestamp = 0;
const CACHE_DURATION = 30000; // 30 seconds cache


const getDedupedPupils = (pupils) => {
  const map = new Map();
  pupils.forEach((p) => {
    if (!map.has(p.person_id)) {
      map.set(p.person_id, p);
    }
  });
  return Array.from(map.values());
};

const getFormsList = (pupils) => {
  const forms = new Set();
  pupils.forEach((p) => p.form && forms.add(p.form));
  return Array.from(forms).sort();
};

// Helper function to get nationality from student object (handles both 'nationality' and 'nationalities' fields)
const getNationality = (student) => {
  if (!student) return 'Unknown';
  
  // First try 'nationality' field (singular)
  if (student.nationality && student.nationality !== 'null' && student.nationality !== 'undefined') {
    return student.nationality;
  }
  
  // Then try 'nationalities' array (plural)
  if (student.nationalities) {
    if (Array.isArray(student.nationalities) && student.nationalities.length > 0) {
      const nat = student.nationalities[0];
      if (nat && nat !== 'null' && nat !== 'undefined') {
        return nat;
      }
    } else if (typeof student.nationalities === 'string' && student.nationalities !== 'null' && student.nationalities !== 'undefined') {
      // Handle case where nationalities is a string instead of array
      return student.nationalities;
    }
  }
  
  return 'Unknown';
};

// AM Registration Report Component
const AMRegistrationReport = ({ 
  students, 
  registrationData, 
  setRegistrationData, 
  isLoadingRegistration, 
  setIsLoadingRegistration 
}) => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [breakdownType, setBreakdownType] = useState('nationality'); // 'nationality', 'gender', 'yeargroup'
  
  const syncRegistrationData = async () => {
    setIsLoadingRegistration(true);
    try {
      // Call the fast bulk iSAMS attendance API
      const response = await axios.get(buildApiUrl(`/attendance/isams-report?date=${selectedDate}`));
      
      if (response.data.success) {
        // Process the new fast API response structure
        const processedRegistration = {};
        
        // The new API returns forms directly with attendance data
        response.data.forms.forEach(formData => {
          const formName = formData.form;
          
          // Map the API response students to our frontend structure
          processedRegistration[formName] = formData.students.map(student => {
            const status = student.status; // 'present', 'late', 'absent'
            const attendanceCode = student.code; // 'P', 'L', or absence code
            
            return {
              id: student.studentId || student.schoolId, // Use studentId or fallback to schoolId
              schoolId: student.schoolId,
              fullName: student.fullName,
              form: student.form,
              nationality: getNationality(student),
              gender: student.gender, // Add gender field
              ncYear: student.ncYear, // Add year group field
              status: status,
              attendanceCode: attendanceCode,
              isPresent: student.isPresent,
              isLate: student.isLate
            };
          });
        });
        
        setRegistrationData(processedRegistration);
        console.log(`✅ Loaded real iSAMS attendance data for ${selectedDate} - Period ${response.data.period_id} - ${response.data.totals.total} students`);
        console.log('📊 Totals:', response.data.totals);
      } else {
        console.error('Failed to fetch iSAMS attendance data:', response.data.error);
        // Show error to user
        alert(`Failed to fetch attendance data: ${response.data.error}`);
        setRegistrationData({});
      }
    } catch (error) {
      console.error('Failed to sync registration data:', error);
      // Show error to user  
      alert(`Error syncing attendance data: ${error.message}`);
      setRegistrationData({});
    } finally {
      setIsLoadingRegistration(false);
    }
  };

  // Get the correct registration period ID for a given date
  const getRegistrationPeriodForDate = (date) => {
    // Historical mapping based on the registration periods data
    const periodMapping = {
      '2025-08-31': 29604,
      '2025-09-01': 29605,
      '2025-09-02': 29606,
      '2025-09-03': 29607,
      '2025-09-04': 29608
    };
    
    return periodMapping[date] || null;
  };

  const getRegistrationStats = () => {
    if (!registrationData || Object.keys(registrationData).length === 0) {
      return { totalStudents: 0, presentStudents: 0, lateStudents: 0, absentStudents: 0, forms: 0 };
    }

    const allStudents = Object.values(registrationData).flat();
    const presentStudents = allStudents.filter(s => s.status === 'present').length;
    const lateStudents = allStudents.filter(s => s.status === 'late').length;
    const absentStudents = allStudents.filter(s => s.status === 'absent').length;
    
    return {
      totalStudents: allStudents.length,
      presentStudents,
      lateStudents,
      absentStudents,
      forms: Object.keys(registrationData).length
    };
  };

  const getAbsentStudentsBreakdown = (type = 'nationality') => {
    if (!registrationData || Object.keys(registrationData).length === 0) {
      return {};
    }

    const absentStudents = Object.values(registrationData).flat().filter(s => s.status === 'absent');
    const breakdownCounts = {};
    
    absentStudents.forEach(student => {
      let category;
      
      switch (type) {
        case 'gender':
          // Extract gender from student data and map M/F to Male/Female
          const rawGender = student.gender || 'Unknown';
          category = rawGender === 'M' ? 'Male' : rawGender === 'F' ? 'Female' : rawGender;
          break;
        case 'yeargroup':
          // Extract year group from form (e.g., "5C" -> "Year 5", "PS Turtles" -> "Pre-School")
          const form = student.form || 'Unknown';
          if (form.startsWith('PS')) {
            category = 'Pre-School';
          } else if (form.startsWith('R ')) {
            category = 'Reception';
          } else {
            const yearMatch = form.match(/^(\d+)/);
            category = yearMatch ? `Year ${yearMatch[1]}` : 'Unknown';
          }
          break;
        case 'nationality':
        default:
          category = student.nationality || 'Unknown';
          break;
      }
      
      breakdownCounts[category] = (breakdownCounts[category] || 0) + 1;
    });

    return breakdownCounts;
  };

  // Get stats based on selected breakdown type
  const getNationalityStats = () => getAbsentStudentsBreakdown('nationality'); // Keep for backward compatibility

  const stats = getRegistrationStats();
  const nationalityStats = getNationalityStats(); // Keep for backward compatibility
  const currentBreakdownStats = getAbsentStudentsBreakdown(breakdownType);
  
  // Get the appropriate title for the breakdown type
  const getBreakdownTitle = () => {
    switch (breakdownType) {
      case 'gender': return 'Absent Students by Gender';
      case 'yeargroup': return 'Absent Students by Year Group';
      case 'nationality':
      default: return 'Absent Students by Nationality';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Controls */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">AM Registration Report</h3>
              <p className="text-gray-600">Real-time attendance data from iSAMS</p>
              <div className="mt-2 text-xs text-gray-500">
                <span className="font-medium">Codes:</span> P/L/✓ = Present, N/M/S/etc = Absent
                {getRegistrationPeriodForDate(selectedDate) && (
                  <span className="ml-4 font-medium text-blue-600">
                    Period ID: {getRegistrationPeriodForDate(selectedDate)}
                  </span>
                )}
                <span className="ml-4 font-medium text-green-600">📡 Live iSAMS Data</span>
              </div>
            </div>
            <div className="flex gap-3">
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
              <button
                onClick={syncRegistrationData}
                disabled={isLoadingRegistration}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2"
              >
                {isLoadingRegistration ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Syncing...
                  </>
                ) : (
                  <>
                    🔄 Sync from iSAMS
                  </>
                )}
              </button>
            </div>
          </div>
        </div>      {/* Stats Cards with Donut Chart */}
      {Object.keys(registrationData).length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 text-sm font-bold">👥</span>
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Total Students</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalStudents}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-600 text-sm font-bold">✅</span>
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Present</p>
                  <p className="text-2xl font-bold text-green-600">{stats.presentStudents}</p>
                  <p className="text-xs text-gray-500">{stats.totalStudents > 0 ? ((stats.presentStudents / stats.totalStudents) * 100).toFixed(1) : 0}%</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                    <span className="text-yellow-600 text-sm font-bold">⏰</span>
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Late</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.lateStudents}</p>
                  <p className="text-xs text-gray-500">{stats.totalStudents > 0 ? ((stats.lateStudents / stats.totalStudents) * 100).toFixed(1) : 0}%</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                    <span className="text-red-600 text-sm font-bold">❌</span>
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Absent</p>
                  <p className="text-2xl font-bold text-red-600">{stats.absentStudents}</p>
                  <p className="text-xs text-gray-500">{stats.totalStudents > 0 ? ((stats.absentStudents / stats.totalStudents) * 100).toFixed(1) : 0}%</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                    <span className="text-purple-600 text-sm font-bold">🏫</span>
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Forms</p>
                  <p className="text-2xl font-bold text-purple-600">{stats.forms}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Donut Chart */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">Attendance Overview</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={[
                    { name: 'Present', value: stats.presentStudents, color: '#10b981' },
                    { name: 'Absent', value: stats.absentStudents, color: '#ef4444' }
                  ]}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  <Cell fill="#10b981" />
                  <Cell fill="#ef4444" />
                </Pie>
                <Tooltip formatter={(value) => `${value} students`} />
                <Legend 
                  verticalAlign="bottom" 
                  height={36}
                  formatter={(value, entry) => {
                    const data = entry.payload;
                    return `${value}: ${data.value} (${((data.value / stats.totalStudents) * 100).toFixed(1)}%)`;
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Registration Tables and Charts */}
      {Object.keys(registrationData).length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Registration Table */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Registration by Form</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Form</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Present</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Absent</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Attendance %</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {Object.entries(registrationData).map(([form, formStudents]) => {
                      const total = formStudents.length;
                      const present = formStudents.filter(s => s.status === 'present').length;
                      const absent = formStudents.filter(s => s.status === 'absent').length;
                      const percentage = ((present / total) * 100).toFixed(1);
                      
                      return (
                        <tr key={form}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{form}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{total}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">{present}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">{absent}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div className="flex items-center">
                              <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                                <div
                                  className="bg-green-600 h-2 rounded-full"
                                  style={{ width: `${percentage}%` }}
                                ></div>
                              </div>
                              <span className="text-sm font-medium">{percentage}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Dynamic Breakdown Chart */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{getBreakdownTitle()}</h3>
              <select 
                value={breakdownType} 
                onChange={(e) => setBreakdownType(e.target.value)}
                className="text-sm border border-gray-300 rounded-md px-3 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="nationality">Nationality</option>
                <option value="gender">Gender</option>
                <option value="yeargroup">Year Group</option>
              </select>
            </div>
            {Object.keys(currentBreakdownStats).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(currentBreakdownStats)
                  .sort(([,a], [,b]) => b - a)
                  .map(([category, count], index) => {
                    const total = Object.values(currentBreakdownStats).reduce((sum, val) => sum + val, 0);
                    const percentage = ((count / total) * 100).toFixed(1);
                    const colors = ['bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-purple-500', 'bg-pink-500', 'bg-indigo-500'];
                    const color = colors[index % colors.length];
                    
                    return (
                      <div key={category} className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className={`w-3 h-3 rounded-full ${color} mr-2`}></div>
                          <span className="text-sm text-gray-700">{category}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">{count}</span>
                          <span className="text-xs text-gray-500">({percentage}%)</span>
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">📊</span>
                </div>
                <p>No absent students to display</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">📊</span>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Registration Data</h3>
          <p className="text-gray-500 mb-4">Click "Sync Registration" to load attendance data for the selected date.</p>
          <button
            onClick={syncRegistrationData}
            disabled={isLoadingRegistration}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400"
          >
            {isLoadingRegistration ? 'Syncing...' : 'Sync Registration'}
          </button>
        </div>
      )}

      {/* Detailed Student List */}
      {Object.keys(registrationData).length > 0 && (
        <div className="mt-6 bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Detailed Student Registration</h3>
            <p className="text-sm text-gray-600 mt-1">Individual student attendance records with codes</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Form</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nationality</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Object.entries(registrationData).flatMap(([form, formStudents]) => 
                  formStudents.map(student => (
                    <tr key={`${form}-${student.id}`} className={student.status === 'absent' ? 'bg-red-50' : ''}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {student.full_name}
                        <div className="text-xs text-gray-500">ID: {student.school_id}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{form}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          student.status === 'present' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {student.status === 'present' ? '✅ Present' : '❌ Absent'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                        {student.attendanceCode || 'N'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {getNationality(student)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Pupils Overview Report Component (Dashboard with Age, Nationality, Gender)
const PupilsOverviewReport = ({ students }) => {
  const [selectedSchool, setSelectedSchool] = useState('all');
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedForm, setSelectedForm] = useState('all');

  // Get unique years and forms for filters
  const years = React.useMemo(() => {
    const yearSet = new Set();
    students.forEach(student => {
      if (student.nc_year !== null && student.nc_year !== undefined) {
        yearSet.add(student.nc_year);
      }
    });
    return Array.from(yearSet).sort((a, b) => Number(a) - Number(b));
  }, [students]);

  const forms = React.useMemo(() => {
    const formSet = new Set();
    
    // Filter forms based on selected year and school first
    students.forEach(student => {
      if (student.form) {
        // Apply year filter if selected
        if (selectedYear !== 'all' && String(student.nc_year) !== String(selectedYear)) {
          return; // Skip this student
        }
        
        // Apply school filter if selected
        if (selectedSchool !== 'all') {
          const divisionName = student.division_name?.toLowerCase() || '';
          const divisionId = student.division_id;
          let matchesSchool = false;
          
          switch (selectedSchool) {
            case 'bani-hajer':
              matchesSchool = divisionName.includes('bani hajer');
              break;
            case 'moq-senior':
              matchesSchool = student.school === 'Main School' && divisionName.includes('seniors') && divisionId === 3;
              break;
            case 'moq-prep':
              matchesSchool = divisionName.includes('prep mall of qatar');
              break;
            case 'qetaf':
              matchesSchool = divisionName.includes('qetaf');
              break;
            case 'boys-school':
              matchesSchool = divisionName.includes('boys');
              break;
            case 'girls-school':
              matchesSchool = student.school === 'Girls School';
              break;
            default:
              matchesSchool = true;
          }
          
          if (!matchesSchool) return; // Skip this student
        }
        
        formSet.add(student.form);
      }
    });
    return Array.from(formSet).sort();
  }, [students, selectedYear, selectedSchool]);

  // Filter students based on school (by year group range), year and form
  const filteredStudents = React.useMemo(() => {
    const filtered = students.filter(student => {
      // First check year and form filters
      // nc_year is a STRING in the data, so compare as strings
      const yearMatch = selectedYear === 'all' || String(student.nc_year) === String(selectedYear);
      const formMatch = selectedForm === 'all' || student.form === selectedForm;
      
      // If year/form don't match, filter out immediately
      if (!yearMatch || !formMatch) return false;
      
      // Then apply school filter using division_name
      if (selectedSchool !== 'all') {
        const divisionName = student.division_name?.toLowerCase() || '';
        const divisionId = student.division_id;
        
        switch (selectedSchool) {
          case 'bani-hajer':
            if (!divisionName.includes('bani hajer')) return false;
            break;
          case 'moq-senior':
            if (student.school !== 'Main School' || !divisionName.includes('seniors') || divisionId !== 3) return false;
            break;
          case 'moq-prep':
            if (!divisionName.includes('prep mall of qatar')) return false;
            break;
          case 'qetaf':
            if (!divisionName.includes('qetaf')) return false;
            break;
          case 'boys-school':
            if (!divisionName.includes('boys')) return false;
            break;
          case 'girls-school':
            // Girls School: Check the school field
            if (student.school !== 'Girls School') return false;
            break;
          default:
            break;
        }
      }
      
      return true;
    });
    
    return filtered;
  }, [students, selectedSchool, selectedYear, selectedForm]);

  // Calculate age with years and months from DOB
  const calculateAge = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    
    let years = today.getFullYear() - birthDate.getFullYear();
    let months = today.getMonth() - birthDate.getMonth();
    
    if (months < 0 || (months === 0 && today.getDate() < birthDate.getDate())) {
      years--;
      months += 12;
    }
    
    if (today.getDate() < birthDate.getDate()) {
      months--;
      if (months < 0) {
        months += 12;
        years--;
      }
    }
    
    // Return age with both display format and sortable total months
    // Display: 10.11 means 10 years 11 months
    // totalMonths: used for proper sorting (10y 11m = 131 months > 10y 2m = 122 months)
    return {
      display: parseFloat(`${years}.${months.toString().padStart(2, '0')}`),
      totalMonths: years * 12 + months
    };
  };

  // Prepare age data with student names
  const ageChartData = filteredStudents
    .map(student => {
      const ageData = calculateAge(student.dob);
      return {
        name: student.full_name,
        age: ageData ? ageData.display : 0,
        totalMonths: ageData ? ageData.totalMonths : 0
      };
    })
    .filter(item => item.age > 0)
    .sort((a, b) => b.totalMonths - a.totalMonths); // Sort by total months for correct age ordering

  // Prepare nationality data
  const nationalityData = {};
  filteredStudents.forEach(student => {
    const nationality = getNationality(student);
    nationalityData[nationality] = (nationalityData[nationality] || 0) + 1;
  });

  const nationalityChartData = Object.entries(nationalityData)
    .sort(([, a], [, b]) => b - a)
    .map(([nationality, count]) => ({
      nationality: nationality,
      count: count
    }));

  // Prepare gender data
  const genderData = {
    Male: filteredStudents.filter(s => s.gender === 'M').length,
    Female: filteredStudents.filter(s => s.gender === 'F').length
  };

  const genderChartData = [
    { name: 'Male', value: genderData.Male, color: '#3b82f6' },
    { name: 'Female', value: genderData.Female, color: '#ec4899' }
  ];

  // Prepare academic house data
  const HOUSE_COLORS = {
    'Amna': '#10b981',
    'Jassim': '#8b5cf6',
    'Shepperd': '#3b82f6',
    'Powys': '#eab308',
    'Irons': '#ef4444',
    'Khalifa': '#06b6d4'
  };

  const houseData = {};
  filteredStudents.forEach(student => {
    const house = student.academic_house || 'Unknown';
    houseData[house] = (houseData[house] || 0) + 1;
  });

  const houseChartData = Object.entries(houseData)
    .map(([house, count]) => ({
      name: house,
      value: count,
      color: HOUSE_COLORS[house] || '#9ca3af'
    }))
    .sort((a, b) => b.value - a.value);

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16', '#f97316'];

  return (
    <div className="space-y-6">
      {/* Header with Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Pupils Overview</h2>
        <p className="text-gray-600 mb-4">Overview of {filteredStudents.length} students</p>
        
        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">School</label>
            <select
              value={selectedSchool}
              onChange={(e) => {
                setSelectedSchool(e.target.value);
                setSelectedYear('all'); // Reset year when changing school
                setSelectedForm('all'); // Reset form when changing school
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="all">All Schools</option>
              <option value="bani-hajer">Bani Hajer</option>
              <option value="boys-school">Boys School</option>
              <option value="qetaf">Qetaf</option>
              <option value="moq-senior">MOQ Senior</option>
              <option value="moq-prep">MOQ Prep</option>
              <option value="girls-school">Girls School</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Year Group</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="all">All Years</option>
              {selectedSchool === 'all' && (
                <>
                  <option value="-1">Pre-School</option>
                  <option value="0">Reception</option>
                  <option value="1">Year 1</option>
                  <option value="2">Year 2</option>
                  <option value="3">Year 3</option>
                  <option value="4">Year 4</option>
                  <option value="5">Year 5</option>
                  <option value="6">Year 6</option>
                  <option value="7">Year 7</option>
                  <option value="8">Year 8</option>
                  <option value="9">Year 9</option>
                  <option value="10">Year 10</option>
                  <option value="11">Year 11</option>
                  <option value="12">Year 12</option>
                  <option value="13">Year 13</option>
                </>
              )}
              {selectedSchool === 'bani-hajer' && (
                <>
                  <option value="-1">Pre-School</option>
                  <option value="0">Reception</option>
                  <option value="1">Year 1</option>
                  <option value="2">Year 2</option>
                  <option value="3">Year 3</option>
                  <option value="4">Year 4</option>
                  <option value="5">Year 5</option>
                  <option value="6">Year 6</option>
                </>
              )}
              {selectedSchool === 'moq-senior' && (
                <>
                  <option value="7">Year 7</option>
                  <option value="8">Year 8</option>
                  <option value="9">Year 9</option>
                  <option value="10">Year 10</option>
                  <option value="11">Year 11</option>
                  <option value="12">Year 12</option>
                  <option value="13">Year 13</option>
                </>
              )}
              {selectedSchool === 'moq-prep' && (
                <>
                  <option value="15">Pre-School</option>
                  <option value="16">Reception</option>
                  <option value="17">Year 1</option>
                  <option value="18">Year 2</option>
                  <option value="19">Year 3</option>
                  <option value="20">Year 4</option>
                  <option value="21">Year 5</option>
                  <option value="22">Year 6</option>
                </>
              )}
              {selectedSchool === 'qetaf' && (
                <>
                  <option value="26">Reception</option>
                  <option value="27">Year 1</option>
                  <option value="28">Year 2</option>
                  <option value="29">Year 3</option>
                  <option value="30">Year 4</option>
                  <option value="31">Year 5</option>
                  <option value="32">Year 6</option>
                  <option value="33">Year 7</option>
                </>
              )}
              {selectedSchool === 'boys-school' && (
                <>
                  <option value="34">Pre-School</option>
                  <option value="35">Reception</option>
                  <option value="36">Year 1</option>
                  <option value="37">Year 2</option>
                  <option value="38">Year 3</option>
                  <option value="39">Year 4</option>
                  <option value="40">Year 5</option>
                  <option value="41">Year 6</option>
                </>
              )}
              {selectedSchool === 'girls-school' && (
                <>
                  <option value="-1">Pre-School</option>
                  <option value="0">Reception</option>
                  <option value="1">Year 1</option>
                  <option value="2">Year 2</option>
                  <option value="3">Year 3</option>
                  <option value="4">Year 4</option>
                  <option value="5">Year 5</option>
                  <option value="6">Year 6</option>
                  <option value="7">Year 7</option>
                  <option value="8">Year 8</option>
                  <option value="9">Year 9</option>
                  <option value="10">Year 10</option>
                  <option value="11">Year 11</option>
                  <option value="12">Year 12</option>
                </>
              )}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Form</label>
            <select
              value={selectedForm}
              onChange={(e) => setSelectedForm(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="all">All Forms</option>
              {forms.map(form => (
                <option key={form} value={form}>{form}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Total Pupils Card */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-blue-100 text-sm font-medium mb-1">Total Pupils</p>
            <p className="text-4xl font-bold">{filteredStudents.length}</p>
          </div>
          <div className="bg-white/20 rounded-full p-4">
            <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
            </svg>
          </div>
        </div>
      </div>

      {/* Nationality and Gender Charts Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Nationality Horizontal Bar Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Nationality Distribution ({nationalityChartData.length})</h3>
          <div className="overflow-y-auto" style={{ maxHeight: '500px' }}>
            <ResponsiveContainer width="100%" height={Math.max(300, nationalityChartData.length * 40)}>
              <BarChart data={nationalityChartData} layout="vertical" margin={{ left: 20, right: 60 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" allowDecimals={false} />
                <YAxis dataKey="nationality" type="category" width={120} interval={0} tick={{ fontSize: 14 }} />
                <Tooltip formatter={(value) => {
                  const percentage = ((value / filteredStudents.length) * 100).toFixed(1);
                  return [`${value} students (${percentage}%)`, 'Count'];
                }} />
                <Legend />
                <Bar dataKey="count" name="Number of Students">
                  {nationalityChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                  {nationalityChartData.map((entry, index) => {
                    const percentage = ((entry.count / filteredStudents.length) * 100).toFixed(1);
                    return (
                      <text
                        key={`label-${index}`}
                        x="95%"
                        y={index * 40 + 20}
                        fill="#374151"
                        fontSize={12}
                        fontWeight="500"
                        textAnchor="end"
                      >
                        {percentage}%
                      </text>
                    );
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Gender Donut Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Gender Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={genderChartData}
                cx="50%"
                cy="50%"
                innerRadius={80}
                outerRadius={120}
                paddingAngle={2}
                dataKey="value"
                label={false}
              >
                <Cell fill="#3b82f6" />
                <Cell fill="#ec4899" />
              </Pie>
              <Tooltip formatter={(value) => `${value} students`} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-8 mt-4">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-blue-500"></div>
              <span className="text-sm text-gray-700">
                Male: {genderChartData[0]?.value || 0} ({genderChartData[0]?.value ? ((genderChartData[0].value / genderChartData.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(1) : 0}%)
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-pink-500"></div>
              <span className="text-sm text-gray-700">
                Female: {genderChartData[1]?.value || 0} ({genderChartData[1]?.value ? ((genderChartData[1].value / genderChartData.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(1) : 0}%)
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Academic House Donut Chart */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Academic House Distribution</h3>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
          {/* House List */}
          <div className="space-y-3">
            {houseChartData.map((house, index) => {
              const percentage = ((house.value / filteredStudents.length) * 100).toFixed(1);
              return (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: house.color }}></div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-700">{house.name}</div>
                    <div className="text-xs text-gray-500">{house.value} ({percentage}%)</div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Chart */}
          <div className="lg:col-span-2">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={houseChartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={80}
                  outerRadius={120}
                  paddingAngle={2}
                  dataKey="value"
                  label={false}
                >
                  {houseChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `${value} students`} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Age Column Chart */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Age Distribution</h3>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={ageChartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey={selectedForm !== 'all' ? "name" : null}
              angle={-45} 
              textAnchor="end" 
              height={selectedForm !== 'all' ? 120 : 50}
              interval={0}
              tick={selectedForm !== 'all' ? { fontSize: 10 } : false}
            />
            <YAxis 
              label={{ value: 'Age (years.months)', angle: -90, position: 'insideLeft' }}
              domain={[0, 'auto']}
            />
            <Tooltip 
              formatter={(value, name) => {
                if (name === 'age') {
                  return [value, 'Age'];
                }
                return value;
              }}
              labelFormatter={(label) => selectedForm !== 'all' ? label : ''}
            />
            <Legend />
            <Bar 
              dataKey="age" 
              fill="#3b82f6" 
              name="Age" 
              label={selectedForm !== 'all' ? { position: 'top', fontSize: 12, fontWeight: 'bold' } : false}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// School Overview Report Component with Filters
const SchoolOverviewReport = ({ students, registrationData, setRegistrationData, isLoadingRegistration, setIsLoadingRegistration }) => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [useRange, setUseRange] = useState(false);
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedSchool, setSelectedSchool] = useState('all');
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedForm, setSelectedForm] = useState('all');
  const [breakdownType, setBreakdownType] = useState('yeargroup');

  // Helper function to get school from NC year
  const getSchoolFromNC = (ncYear) => {
    const nc = Number(ncYear);
    if (nc >= -1 && nc <= 6) return 'Bani Hajer';
    if (nc >= 7 && nc <= 13) return 'MOQ Senior';
    if (nc >= 15 && nc <= 22) return 'MOQ Prep';
    if (nc >= 26 && nc <= 33) return 'Qetaifan';
    if (nc >= 34 && nc <= 41) return 'Boys School';
    return 'Unknown';
  };

  // Get unique schools, years and forms for filters
  const schools = React.useMemo(() => {
    const schoolSet = new Set();
    Object.values(registrationData).flat().forEach(student => {
      if (student.ncYear !== null && student.ncYear !== undefined) {
        schoolSet.add(getSchoolFromNC(student.ncYear));
      }
    });
    return Array.from(schoolSet).sort();
  }, [registrationData]);

  const years = React.useMemo(() => {
    const yearSet = new Set();
    Object.values(registrationData).flat().forEach(student => {
      if (student.ncYear !== null && student.ncYear !== undefined) {
        yearSet.add(student.ncYear);
      }
    });
    return Array.from(yearSet).sort((a, b) => Number(a) - Number(b));
  }, [registrationData]);

  const forms = React.useMemo(() => {
    const formSet = new Set();
    Object.keys(registrationData).forEach(form => formSet.add(form));
    return Array.from(formSet).sort();
  }, [registrationData]);

  // Export to PDF function
  const exportToPDF = async () => {
    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(18);
    doc.text('Pupil Attendance Report', 14, 20);
    
    // Add metadata
    doc.setFontSize(11);
    doc.text(`Date: ${useRange ? `${startDate} to ${endDate}` : selectedDate}`, 14, 30);
    doc.text(`School: ${selectedSchool === 'all' ? 'All Schools' : selectedSchool}`, 14, 36);
    doc.text(`Year: ${selectedYear === 'all' ? 'All Years' : selectedYear === '-1' ? 'Pre-School' : selectedYear === '0' ? 'Reception' : `Year ${selectedYear}`}`, 14, 42);
    doc.text(`Form: ${selectedForm === 'all' ? 'All Forms' : selectedForm}`, 14, 48);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 54);
    
    // Add summary stats
    doc.setFontSize(14);
    doc.text('Summary Statistics', 14, 64);
    
    const summaryData = [
      ['Total Students', filteredStats.total],
      ['Present', `${filteredStats.present} (${filteredStats.total > 0 ? ((filteredStats.present / filteredStats.total) * 100).toFixed(1) : 0}%)`],
      ['Late', `${filteredStats.late} (${filteredStats.total > 0 ? ((filteredStats.late / filteredStats.total) * 100).toFixed(1) : 0}%)`],
      ['Absent', `${filteredStats.absent} (${filteredStats.total > 0 ? ((filteredStats.absent / filteredStats.total) * 100).toFixed(1) : 0}%)`],
      ['Attendance Rate', `${filteredStats.total > 0 ? (((filteredStats.present + filteredStats.late) / filteredStats.total) * 100).toFixed(1) : 0}%`]
    ];
    
    autoTable(doc, {
      startY: 68,
      head: [['Metric', 'Value']],
      body: summaryData,
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246] }
    });
    
    let currentY = doc.lastAutoTable.finalY + 10;
    
    // Capture and add the pie chart
    const pieChartElement = document.getElementById('attendance-pie-chart');
    if (pieChartElement) {
      try {
        const canvas = await html2canvas(pieChartElement, { 
          scale: 2, 
          backgroundColor: '#ffffff',
          logging: false
        });
        const imgData = canvas.toDataURL('image/png');
        const imgWidth = 180;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        // Check if we need a new page
        if (currentY + imgHeight > 270) {
          doc.addPage();
          currentY = 20;
        }
        
        doc.setFontSize(12);
        doc.text('Attendance Overview Chart', 14, currentY);
        doc.addImage(imgData, 'PNG', 14, currentY + 5, imgWidth, imgHeight);
        currentY += imgHeight + 15;
      } catch (error) {
        console.error('Error capturing pie chart:', error);
      }
    }
    
    // Add breakdown by category
    if (Object.keys(breakdownData).length > 0) {
      // Check if we need a new page for the table
      if (currentY > 200) {
        doc.addPage();
        currentY = 20;
      }
      
      doc.setFontSize(14);
      doc.text(`Absent Students by ${breakdownType === 'nationality' ? 'Nationality' : breakdownType === 'gender' ? 'Gender' : 'Year Group'}`, 14, currentY);
      
      const breakdownTableData = Object.entries(breakdownData)
        .sort(([,a], [,b]) => b - a)
        .map(([category, count]) => [category, count]);
      
      autoTable(doc, {
        startY: currentY + 5,
        head: [['Category', 'Count']],
        body: breakdownTableData,
        theme: 'grid',
        headStyles: { fillColor: [239, 68, 68] }
      });
      
      // Capture and add the breakdown bar chart
      const barChartElement = document.getElementById('attendance-breakdown-chart');
      if (barChartElement) {
        try {
          doc.addPage();
          
          const canvas = await html2canvas(barChartElement, { 
            scale: 2, 
            backgroundColor: '#ffffff',
            logging: false
          });
          const imgData = canvas.toDataURL('image/png');
          const imgWidth = 180;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          
          doc.setFontSize(12);
          doc.text('Breakdown Chart', 14, 20);
          doc.addImage(imgData, 'PNG', 14, 25, imgWidth, Math.min(imgHeight, 240));
        } catch (error) {
          console.error('Error capturing bar chart:', error);
        }
      }
    }
    
    // Save the PDF
    doc.save(`pupil-attendance-${selectedDate}.pdf`);
  };

  // Export to Excel function
  const exportToExcel = () => {
    // Prepare summary data
    const summarySheet = [
      ['Pupil Attendance Report'],
      ['Date', selectedDate],
      ['Year', selectedYear === 'all' ? 'All Years' : selectedYear === '-1' ? 'Pre-School' : selectedYear === '0' ? 'Reception' : `Year ${selectedYear}`],
      ['Form', selectedForm === 'all' ? 'All Forms' : selectedForm],
      ['Generated', new Date().toLocaleString()],
      [],
      ['Summary Statistics'],
      ['Metric', 'Value'],
      ['Total Students', filteredStats.total],
      ['Present', `${filteredStats.present} (${filteredStats.total > 0 ? ((filteredStats.present / filteredStats.total) * 100).toFixed(1) : 0}%)`],
      ['Late', `${filteredStats.late} (${filteredStats.total > 0 ? ((filteredStats.late / filteredStats.total) * 100).toFixed(1) : 0}%)`],
      ['Absent', `${filteredStats.absent} (${filteredStats.total > 0 ? ((filteredStats.absent / filteredStats.total) * 100).toFixed(1) : 0}%)`],
      ['Attendance Rate (Present + Late)', `${filteredStats.total > 0 ? (((filteredStats.present + filteredStats.late) / filteredStats.total) * 100).toFixed(1) : 0}%`]
    ];
    
    // Prepare detailed student data
    const detailedData = [
      [],
      ['Detailed Student List'],
      ['School ID', 'Full Name', 'Form', 'Year', 'Status', 'Attendance Code', 'Nationality', 'Gender']
    ];
    
    filteredStudents.forEach(student => {
      detailedData.push([
        student.schoolId || '',
        student.fullName || '',
        student.form || '',
        getYearLabelUtil(student.ncYear),
        student.status || '',
        student.attendanceCode || '',
        student.nationality || '',
        student.gender === 'M' ? 'Male' : student.gender === 'F' ? 'Female' : student.gender || ''
      ]);
    });
    
    // Prepare breakdown data
    const breakdownSheetData = [
      [],
      [`Absent Students by ${breakdownType === 'nationality' ? 'Nationality' : breakdownType === 'gender' ? 'Gender' : 'Year Group'}`],
      ['Category', 'Count']
    ];
    
    Object.entries(breakdownData)
      .sort(([,a], [,b]) => b - a)
      .forEach(([category, count]) => {
        breakdownSheetData.push([category, count]);
      });
    
    // Combine all data
    const allData = [...summarySheet, ...detailedData, ...breakdownSheetData];
    
    // Create workbook
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(allData);
    
    // Set column widths
    ws['!cols'] = [
      { wch: 15 }, // School ID
      { wch: 25 }, // Full Name
      { wch: 15 }, // Form
      { wch: 12 }, // Year
      { wch: 10 }, // Status
      { wch: 15 }, // Attendance Code
      { wch: 20 }, // Nationality
      { wch: 10 }  // Gender
    ];
    
    XLSX.utils.book_append_sheet(wb, ws, 'Attendance Report');
    XLSX.writeFile(wb, `pupil-attendance-${useRange ? `${startDate}_to_${endDate}` : selectedDate}.xlsx`);
  };

  // Sync data for the whole school
  const syncSchoolData = async () => {
    setIsLoadingRegistration(true);
    try {
      if (useRange) {
        // Fetch data for date range
        console.log(`🔍 Syncing school data for range: ${startDate} to ${endDate}`);
        const dates = getDateRange(startDate, endDate);
        console.log(`📅 Fetching ${dates.length} days of data`);
        
        const allResponses = await Promise.all(
          dates.map(date => 
            axios.get(buildApiUrl(`/attendance/isams-report?date=${date}&_cache_bust=${Date.now()}`))
              .catch(err => ({ data: { success: false, error: err.message } }))
          )
        );
        
        // Merge all data
        const mergedRegistration = {};
        
        allResponses.forEach((response, index) => {
          if (response.data.success) {
            response.data.forms.forEach(formData => {
              const formName = formData.form;
              
              if (!mergedRegistration[formName]) {
                mergedRegistration[formName] = [];
              }
              
              formData.students.forEach(student => {
                mergedRegistration[formName].push({
                  id: student.studentId || student.schoolId,
                  schoolId: student.schoolId,
                  fullName: student.fullName,
                  form: student.form,
                  nationality: getNationality(student),
                  gender: student.gender,
                  ncYear: student.ncYear,
                  status: student.status,
                  attendanceCode: student.code,
                  isPresent: student.isPresent,
                  isLate: student.isLate,
                  date: dates[index]
                });
              });
            });
          }
        });
        
        setRegistrationData(mergedRegistration);
        console.log(`✅ Loaded school-wide attendance for ${startDate} to ${endDate}`, {
          forms: Object.keys(mergedRegistration).length,
          totalRecords: Object.values(mergedRegistration).flat().length
        });
      } else {
        // Single date fetch
        console.log(`🔍 Syncing school data for date: ${selectedDate}`);
        const timestamp = Date.now();
        const response = await axios.get(buildApiUrl(`/attendance/isams-report?date=${selectedDate}&_cache_bust=${timestamp}`));
        
        if (response.data.success) {
          console.log(`✅ Received data for date: ${selectedDate}, Period ID: ${response.data.period_id}`, response.data);
          const processedRegistration = {};
          
          response.data.forms.forEach(formData => {
            const formName = formData.form;
            
            processedRegistration[formName] = formData.students.map(student => ({
              id: student.studentId || student.schoolId,
              schoolId: student.schoolId,
              fullName: student.fullName,
              form: student.form,
              nationality: getNationality(student),
              gender: student.gender,
              ncYear: student.ncYear,
              status: student.status,
              attendanceCode: student.code,
              isPresent: student.isPresent,
              isLate: student.isLate,
              date: selectedDate
            }));
          });
          
          setRegistrationData(processedRegistration);
          console.log(`✅ Loaded school-wide attendance for ${selectedDate}`, {
            forms: Object.keys(processedRegistration).length,
            totalStudents: Object.values(processedRegistration).flat().length
          });
        }
      }
    } catch (error) {
      console.error('Failed to sync school data:', error);
      alert(`Error syncing data: ${error.message}`);
      setRegistrationData({});
    } finally {
      setIsLoadingRegistration(false);
    }
  };

  // Helper function to get date range
  const getDateRange = (start, end) => {
    const dates = [];
    const startDt = new Date(start);
    const endDt = new Date(end);
    
    let currentDate = new Date(startDt);
    while (currentDate <= endDt) {
      // Skip weekends (Friday=5, Saturday=6)
      const dayOfWeek = currentDate.getDay();
      if (dayOfWeek !== 5 && dayOfWeek !== 6) { // Not Friday or Saturday
        dates.push(currentDate.toISOString().split('T')[0]);
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return dates;
  };

  // Filter students based on selections
  const filteredStudents = React.useMemo(() => {
    let allStudents = Object.values(registrationData).flat();
    
    if (selectedSchool !== 'all') {
      allStudents = allStudents.filter(s => {
        if (s.ncYear !== null && s.ncYear !== undefined) {
          return getSchoolFromNC(s.ncYear) === selectedSchool;
        }
        return false;
      });
    }
    
    if (selectedYear !== 'all') {
      allStudents = allStudents.filter(s => String(s.ncYear) === String(selectedYear));
    }
    
    if (selectedForm !== 'all') {
      allStudents = allStudents.filter(s => s.form === selectedForm);
    }
    
    return allStudents;
  }, [registrationData, selectedSchool, selectedYear, selectedForm]);

  // Calculate filtered stats
  const filteredStats = React.useMemo(() => {
    const total = filteredStudents.length;
    const present = filteredStudents.filter(s => s.status === 'present').length;
    const late = filteredStudents.filter(s => s.status === 'late').length;
    const absent = filteredStudents.filter(s => s.status === 'absent').length;
    
    return { total, present, late, absent };
  }, [filteredStudents]);

  // Get breakdown data
  const getBreakdown = () => {
    const absentStudents = filteredStudents.filter(s => s.status === 'absent');
    const breakdownCounts = {};
    
    absentStudents.forEach(student => {
      let category;
      
      switch (breakdownType) {
        case 'gender':
          const rawGender = student.gender || 'Unknown';
          category = rawGender === 'M' ? 'Male' : rawGender === 'F' ? 'Female' : rawGender;
          break;
        case 'yeargroup':
          const form = student.form || 'Unknown';
          if (form.startsWith('PS')) {
            category = 'Pre-School';
          } else if (form.startsWith('R ')) {
            category = 'Reception';
          } else {
            const yearMatch = form.match(/^(\d+)/);
            category = yearMatch ? `Year ${yearMatch[1]}` : 'Unknown';
          }
          break;
        case 'nationality':
        default:
          category = getNationality(student);
          break;
      }
      
      breakdownCounts[category] = (breakdownCounts[category] || 0) + 1;
    });

    return breakdownCounts;
  };

  const breakdownData = getBreakdown();

  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">School Overview Report</h3>
            <p className="text-gray-600">Whole school attendance with year and form filters</p>
          </div>
          <div className="flex gap-3 flex-wrap items-center">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={useRange}
                onChange={(e) => setUseRange(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              Date Range
            </label>
            
            {useRange ? (
              <>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">From:</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">To:</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </>
            ) : (
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            )}
            
            <button
              onClick={syncSchoolData}
              disabled={isLoadingRegistration}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center gap-2"
            >
              {isLoadingRegistration ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Syncing...
                </>
              ) : (
                <>
                  🔄 Sync School Data
                </>
              )}
            </button>
            {Object.keys(registrationData).length > 0 && (
              <>
                <button
                  onClick={exportToPDF}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
                >
                  📄 Export PDF
                </button>
                <button
                  onClick={exportToExcel}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                >
                  📊 Export Excel
                </button>
              </>
            )}
          </div>
        </div>
        
        {/* Filters */}
        {Object.keys(registrationData).length > 0 && (
          <div className="flex gap-4 pt-4 border-t">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">School</label>
              <select
                value={selectedSchool}
                onChange={(e) => setSelectedSchool(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Schools</option>
                {schools.map(school => (
                  <option key={school} value={school}>
                    {school}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Year Group</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Years</option>
                {years.map(year => (
                  <option key={year} value={year}>
                    {year === '-1' ? 'Pre-School' : year === '0' ? 'Reception' : `Year ${year}`}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Form</label>
              <select
                value={selectedForm}
                onChange={(e) => setSelectedForm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Forms</option>
                {forms.map(form => (
                  <option key={form} value={form}>{form}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Stats Cards with Donut Chart */}
      {Object.keys(registrationData).length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 text-sm font-bold">👥</span>
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Total Students</p>
                  <p className="text-2xl font-bold text-gray-900">{filteredStats.total}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-600 text-sm font-bold">✅</span>
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Present</p>
                  <p className="text-2xl font-bold text-green-600">{filteredStats.present}</p>
                  <p className="text-xs text-gray-500">{filteredStats.total > 0 ? ((filteredStats.present / filteredStats.total) * 100).toFixed(1) : 0}%</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                    <span className="text-yellow-600 text-sm font-bold">⏰</span>
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Late</p>
                  <p className="text-2xl font-bold text-yellow-600">{filteredStats.late}</p>
                  <p className="text-xs text-gray-500">{filteredStats.total > 0 ? ((filteredStats.late / filteredStats.total) * 100).toFixed(1) : 0}%</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                    <span className="text-red-600 text-sm font-bold">❌</span>
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Absent</p>
                  <p className="text-2xl font-bold text-red-600">{filteredStats.absent}</p>
                  <p className="text-xs text-gray-500">{filteredStats.total > 0 ? ((filteredStats.absent / filteredStats.total) * 100).toFixed(1) : 0}%</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                    <span className="text-purple-600 text-sm font-bold">%</span>
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Attendance Rate</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {filteredStats.total > 0 ? (((filteredStats.present + filteredStats.late) / filteredStats.total) * 100).toFixed(1) : 0}%
                  </p>
                  <p className="text-xs text-gray-500">Present + Late</p>
                </div>
              </div>
            </div>
          </div>

          {/* Donut Chart */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">Attendance Overview</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={[
                    { name: 'Present', value: filteredStats.present, color: '#10b981' },
                    { name: 'Absent', value: filteredStats.absent, color: '#ef4444' }
                  ]}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  <Cell fill="#10b981" />
                  <Cell fill="#ef4444" />
                </Pie>
                <Tooltip formatter={(value) => `${value} students`} />
                <Legend 
                  verticalAlign="bottom" 
                  height={36}
                  formatter={(value, entry) => {
                    const data = entry.payload;
                    return `${value}: ${data.value} (${((data.value / filteredStats.total) * 100).toFixed(1)}%)`;
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Breakdown Chart */}
      {Object.keys(registrationData).length > 0 && (
        <div className="bg-white rounded-lg shadow p-6" id="attendance-breakdown-chart">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {breakdownType === 'nationality' ? 'Absent by Nationality' : 
               breakdownType === 'gender' ? 'Absent by Gender' : 'Absent by Year Group'}
            </h3>
            <select 
              value={breakdownType} 
              onChange={(e) => setBreakdownType(e.target.value)}
              className="text-sm border border-gray-300 rounded-md px-3 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="yeargroup">Year Group</option>
              <option value="nationality">Nationality</option>
              <option value="gender">Gender</option>
            </select>
          </div>
          {Object.keys(breakdownData).length > 0 ? (
            <ResponsiveContainer width="100%" height={Math.max(350, Object.keys(breakdownData).length * 50)}>
              <BarChart 
                data={Object.entries(breakdownData)
                  .sort(([,a], [,b]) => b - a)
                  .map(([category, count]) => ({ category, count }))}
                layout="vertical"
                margin={{ top: 20, right: 40, left: 150, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  type="number"
                  allowDecimals={false}
                />
                <YAxis 
                  dataKey="category" 
                  type="category" 
                  width={140}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip />
                <Bar 
                  dataKey="count" 
                  fill="#ef4444" 
                  radius={[0, 8, 8, 0]}
                  label={{ position: 'right', fontSize: 12 }}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center text-gray-500 py-8">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">📊</span>
              </div>
              <p className="font-medium">No absent students to display</p>
              <p className="text-sm mt-2">All students are present or late</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Capacity and Availability Report Component
const CapacityReport = ({ students, applicantsData, fetchApplicants, isLoadingApplicants }) => {
  const [selectedSchool, setSelectedSchool] = useState('All Schools');
  const [schoolCapacity, setSchoolCapacity] = useState({});
  const [loadingCapacity, setLoadingCapacity] = useState(true);
  const [editingRow, setEditingRow] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState(false);

  // Fetch capacity data from API
  useEffect(() => {
    const fetchCapacityData = async () => {
      try {
        setLoadingCapacity(true);
        const response = await axios.get(buildApiUrl('/api/capacity/data'));
        if (response.data.success) {
          setSchoolCapacity(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching capacity data:', error);
      } finally {
        setLoadingCapacity(false);
      }
    };
    fetchCapacityData();
  }, []);

  // Save edited capacity values
  const saveCapacityChanges = async (yearCode) => {
    try {
      setSaving(true);
      const response = await axios.post(buildApiUrl('/api/capacity/update'), {
        school: selectedSchool,
        yearCode: yearCode,
        capacity: parseInt(editValues.capacity),
        maxCapacity: parseInt(editValues.maxCapacity)
      });
      
      if (response.data.success) {
        alert('✅ Capacity updated successfully!');
        setEditingRow(null);
        setEditValues({});
        // Refetch capacity data instead of reloading page
        const capacityResponse = await axios.get(buildApiUrl('/api/capacity/data'));
        if (capacityResponse.data.success) {
          setSchoolCapacity(capacityResponse.data.data);
        }
      }
    } catch (error) {
      console.error('Error updating capacity:', error);
      alert('❌ Failed to update capacity');
    } finally {
      setSaving(false);
    }
  };

  const startEditing = (row) => {
    setEditingRow(row.yearCode);
    setEditValues({
      capacity: row.capacity,
      maxCapacity: row.maxCapacity
    });
  };

  const cancelEditing = () => {
    setEditingRow(null);
    setEditValues({});
  };

  // Get year group mapping for easier lookup
  const getYearGroupNumber = (yearGroup) => {
    if (!yearGroup) return null;
    
    // If it's already a number, return it
    if (typeof yearGroup === 'number') return yearGroup;
    
    // If it's a string, try to extract the number
    if (typeof yearGroup === 'string') {
      // Try to match NC format (e.g., "NC1", "NC-1")
      const ncMatch = yearGroup.match(/NC(-?\d+)/);
      if (ncMatch) return parseInt(ncMatch[1]);
      
      // Try to parse as a direct number string
      const parsed = parseInt(yearGroup);
      if (!isNaN(parsed)) return parsed;
    }
    
    return null;
  };

  // Calculate current pupils for each year group of a specific school
  const getCurrentPupilsCount = (schoolName, yearGroupCode) => {
    const yearCode = parseInt(yearGroupCode);

    const filteredPupils = students.filter(pupil => {
      const pupilSchool = pupil.school || '';
      const divisionName = pupil.division_name?.toLowerCase() || '';
      const divisionId = pupil.division_id;
      const pupilForm = pupil.form || '';
      
      // Special handling for Qetaf - count by form instead of year
      if (schoolName === 'Qetaf') {
        // Map yearGroupCode to form name based on schoolCapacity.json
        const qetafFormMapping = {
          '26': 'Kingfisher',  // KS1 Year 1Q and RQ - Kingfisher
          '27': 'Robin',        // KS1 Year 2Q - Robin
          '28': 'Flamingo',     // KS1 Year 2,3 and 4 - Flamingo
          '29': 'Crane',        // KS2 Year 1, 3, and 4 - Crane
          '30': 'Owl',          // KS2 Year 4,5,6 and 6 - Owl
          '31': 'Eagle'         // KS2 Year 5,6, and 7 - Eagle
        };
        
        const targetForm = qetafFormMapping[yearCode.toString()];
        if (!targetForm) return false;
        
        return pupilSchool === 'Main School' && 
               divisionName.includes('qetaf') && 
               pupilForm.toLowerCase().includes(targetForm.toLowerCase());
      }
      
      // For all other schools, use year-based counting
      const studentYearGroup = getYearGroupNumber(
        pupil.enrolmentSchoolYearGroup || pupil.yearGroup || pupil.nc_year || pupil.ncYear
      );
      
      if (studentYearGroup !== yearCode) return false;
      
      if (schoolName === 'Bani Hajer') {
        return pupilSchool === 'Main School' && yearCode >= -1 && yearCode <= 6 && divisionName.includes('bani hajer');
      } else if (schoolName === 'Girls School') {
        return pupilSchool === 'Girls School';
      } else if (schoolName === 'MOQ Senior') {
        return pupilSchool === 'Main School' && yearCode >= 7 && yearCode <= 13 && divisionName.includes('seniors') && divisionId === 3;
      } else if (schoolName === 'MOQ Prep') {
        return pupilSchool === 'Main School' && yearCode >= 15 && yearCode <= 22 && divisionName.includes('prep mall of qatar');
      } else if (schoolName === 'Boys School') {
        return pupilSchool === 'Main School' && yearCode >= 34 && yearCode <= 41 && divisionName.includes('boys');
      }
      
      return false;
    });

    // Remove duplicates based on unique identifiers (pupil_id, person_id, or school_id)
    const uniquePupils = Array.from(
      new Map(
        filteredPupils.map(pupil => [
          pupil.pupil_id || pupil.person_id || pupil.school_id || pupil.id,
          pupil
        ])
      ).values()
    );

    return uniquePupils.length;
  };

  // Calculate current pupils for a school (all year groups)
  const getCurrentPupilsForSchool = (schoolName) => {
    const schoolData = schoolCapacity[schoolName] || {};
    const yearGroups = Object.keys(schoolData).filter(key => key !== 'total');
    
    let total = 0;
    yearGroups.forEach(yearCode => {
      const count = getCurrentPupilsCount(schoolName, yearCode);
      total += count;
    });
    
    return total;
  };

  // Define schools in order
  const schools = [
    { name: 'MOQ Prep', displayName: 'Prep MoQ' },
    { name: 'Bani Hajer', displayName: 'BH' },
    { name: 'Boys School', displayName: 'Boys' },
    { name: 'Qetaf', displayName: 'Qetaf' },
    { name: 'Girls School', displayName: 'Girls' }
  ];

  // All Schools View Data
  const allSchoolsData = schools.map(school => {
    const schoolData = schoolCapacity[school.name] || {};
    const totalData = schoolData.total || { capacity: 0, maxCapacity: 0 };
    const currentPupils = getCurrentPupilsForSchool(school.name);
    const availability = totalData.maxCapacity - currentPupils;

    return {
      name: school.displayName,
      capacity: totalData.capacity,
      maxCapacity: totalData.maxCapacity,
      currentPupils,
      availability
    };
  });

  const grandTotalCurrent = allSchoolsData.reduce((sum, s) => sum + s.currentPupils, 0);
  const grandTotalMaxCapacity = allSchoolsData.reduce((sum, s) => sum + s.maxCapacity, 0);
  const grandTotalAvailability = allSchoolsData.reduce((sum, s) => sum + s.availability, 0);

  // Individual School View Data
  const schoolData = schoolCapacity[selectedSchool] || {};
  const yearGroups = Object.keys(schoolData).filter(key => key !== 'total');

  const individualSchoolData = yearGroups.map(yearCode => {
    const yearData = schoolData[yearCode];
    const currentPupils = getCurrentPupilsCount(selectedSchool, yearCode);
    const availability = yearData.maxCapacity - currentPupils;

    return {
      yearCode,
      name: yearData.name,
      capacity: yearData.capacity,
      maxCapacity: yearData.maxCapacity,
      currentPupils,
      availability
    };
  });

  const schoolTotalCurrent = individualSchoolData.reduce((sum, y) => sum + y.currentPupils, 0);
  const schoolTotalMaxCapacity = individualSchoolData.reduce((sum, y) => sum + y.maxCapacity, 0);
  const schoolTotalAvailability = individualSchoolData.reduce((sum, y) => sum + y.availability, 0);

  return (
    <div className="space-y-6">
      {loadingCapacity ? (
        <div className="text-center py-8">Loading capacity data...</div>
      ) : (
        <>
          {/* School Selector and Refresh Button */}
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-gray-700">Select School:</label>
              <select
                value={selectedSchool}
                onChange={(e) => setSelectedSchool(e.target.value)}
                className="border rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="All Schools">All Schools</option>
                <option value="Bani Hajer">Bani Hajer</option>
                <option value="MOQ Senior">MOQ Senior</option>
            <option value="MOQ Prep">MOQ Prep</option>
            <option value="Boys School">Boys School</option>
            <option value="Girls School">Girls School</option>
            <option value="Qetaf">Qetaf</option>
          </select>
          <button
            onClick={() => fetchApplicants(true)}
            disabled={isLoadingApplicants}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isLoadingApplicants ? (
              <>
                <span className="animate-spin">⟳</span>
                Refreshing...
              </>
            ) : (
              <>
                🔄 Refresh Data
              </>
            )}
          </button>
        </div>
      </div>

      {/* All Schools View */}
      {selectedSchool === 'All Schools' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-yellow-300">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-bold text-gray-900 border border-gray-400">School</th>
                  <th className="px-6 py-3 text-center text-sm font-bold text-gray-900 border border-gray-400">Total of Pupils as per iSAMS (Current pupils ONLY)</th>
                  <th className="px-6 py-3 text-center text-sm font-bold text-gray-900 border border-gray-400">Target</th>
                  <th className="px-6 py-3 text-center text-sm font-bold text-gray-900 border border-gray-400">Availability</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {allSchoolsData.map((row, index) => (
                  <tr key={row.name} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-3 text-sm font-semibold text-gray-900 border border-gray-300">{row.name}</td>
                    <td className="px-6 py-3 text-sm text-center text-gray-900 border border-gray-300 bg-orange-100">{row.currentPupils}</td>
                    <td className="px-6 py-3 text-sm text-center text-gray-900 border border-gray-300 bg-yellow-100">{row.maxCapacity}</td>
                    <td className="px-6 py-3 text-sm text-center font-bold text-gray-900 border border-gray-300 bg-yellow-100">{row.availability}</td>
                  </tr>
                ))}
                <tr className="bg-yellow-200 font-bold">
                  <td className="px-6 py-3 text-sm text-gray-900 border border-gray-400">Total</td>
                  <td className="px-6 py-3 text-sm text-center text-gray-900 border border-gray-400 bg-orange-200">{grandTotalCurrent}</td>
                  <td className="px-6 py-3 text-sm text-center text-gray-900 border border-gray-400">{grandTotalMaxCapacity}</td>
                  <td className="px-6 py-3 text-sm text-center text-gray-900 border border-gray-400">{grandTotalAvailability}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Individual School View */}
      {selectedSchool !== 'All Schools' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 bg-blue-600 text-white">
            <h2 className="text-2xl font-bold">{selectedSchool}</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-yellow-300">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-bold text-gray-900 border border-gray-400">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-bold text-gray-900 border border-gray-400">Capacity</th>
                  <th className="px-6 py-3 text-center text-sm font-bold text-gray-900 border border-gray-400">Max Capacity</th>
                  <th className="px-6 py-3 text-center text-sm font-bold text-gray-900 border border-gray-400">Availability</th>
                  <th className="px-6 py-3 text-center text-sm font-bold text-gray-900 border border-gray-400 bg-orange-300">Current Pupils on iSAMS</th>
                  <th className="px-6 py-3 text-center text-sm font-bold text-gray-900 border border-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {individualSchoolData.map((row, index) => (
                  <tr key={row.yearCode} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-3 text-sm text-gray-900 border border-gray-300">{row.name}</td>
                    <td className="px-6 py-3 text-sm text-center text-gray-900 border border-gray-300 bg-yellow-100">
                      {editingRow === row.yearCode ? (
                        <input
                          type="number"
                          value={editValues.capacity}
                          onChange={(e) => setEditValues({...editValues, capacity: e.target.value})}
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-center"
                        />
                      ) : (
                        row.capacity
                      )}
                    </td>
                    <td className="px-6 py-3 text-sm text-center text-gray-900 border border-gray-300 bg-yellow-100">
                      {editingRow === row.yearCode ? (
                        <input
                          type="number"
                          value={editValues.maxCapacity}
                          onChange={(e) => setEditValues({...editValues, maxCapacity: e.target.value})}
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-center"
                        />
                      ) : (
                        row.maxCapacity
                      )}
                    </td>
                    <td className="px-6 py-3 text-sm text-center font-bold text-gray-900 border border-gray-300 bg-yellow-100">{row.availability}</td>
                    <td className="px-6 py-3 text-sm text-center text-gray-900 border border-gray-300 bg-orange-100">{row.currentPupils}</td>
                    <td className="px-6 py-3 text-sm text-center border border-gray-300">
                      {editingRow === row.yearCode ? (
                        <div className="flex gap-1 justify-center">
                          <button
                            onClick={() => saveCapacityChanges(row.yearCode)}
                            disabled={saving}
                            className="px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 text-xs"
                          >
                            💾 Save
                          </button>
                          <button
                            onClick={cancelEditing}
                            disabled={saving}
                            className="px-2 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:opacity-50 text-xs"
                          >
                            ✖️ Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => startEditing(row)}
                          className="px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs"
                        >
                          ✏️ Edit
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                <tr className="bg-yellow-200 font-bold">
                  <td className="px-6 py-3 text-sm text-gray-900 border border-gray-400">Total</td>
                  <td className="px-6 py-3 text-sm text-center text-gray-900 border border-gray-400">-</td>
                  <td className="px-6 py-3 text-sm text-center text-gray-900 border border-gray-400">{schoolTotalMaxCapacity}</td>
                  <td className="px-6 py-3 text-sm text-center text-gray-900 border border-gray-400">{schoolTotalAvailability}</td>
                  <td className="px-6 py-3 text-sm text-center text-gray-900 border border-gray-400 bg-orange-200">{schoolTotalCurrent}</td>
                  <td className="px-6 py-3 text-sm text-center text-gray-900 border border-gray-400">-</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
};

// Applicants Report Component
const ApplicantsReport = ({ 
  applicantsData,
  setApplicantsData,
  isLoadingApplicants, 
  setIsLoadingApplicants,
  applicantsSchool,
  setApplicantsSchool,
  applicantsSchoolYear,
  setApplicantsSchoolYear,
  applicantsYear,
  setApplicantsYear,
  applicantsMonth,
  setApplicantsMonth,
  applicantsAdmissionStatus = "",
  setApplicantsAdmissionStatus = () => {},
  applicantsStartingYear = "",
  setApplicantsStartingYear = () => {},
  fetchApplicants,
  students = []
}) => {
  const [currentPage, setCurrentPage] = useState(1);

  // Helper function to get year group as number
  const getYearGroupNumber = (yearGroup) => {
    if (!yearGroup) return null;
    const str = String(yearGroup);
    const match = str.match(/-?\d+/);
    return match ? parseInt(match[0]) : null;
  };

  // Filter applicants by school, school year on entry, year, month, admission status, and starting year
  const filteredApplicants = applicantsData.filter(applicant => {
    // Admission status filter
    if (applicantsAdmissionStatus && applicant.admissionStatus !== applicantsAdmissionStatus) {
      return false;
    }
    
    // Starting year filter (enrolmentSchoolYear)
    if (applicantsStartingYear) {
      const startYear = parseInt(applicantsStartingYear);
      if (applicant.isCurrentPupil && applicant.enrolmentDate) {
        const enrolDate = new Date(applicant.enrolmentDate);
        if (!isNaN(enrolDate.getTime())) {
          const month = enrolDate.getMonth();
          const year = enrolDate.getFullYear();
          const academicYear = month >= 8 ? year : year - 1;
          if (academicYear !== startYear) return false;
        }
      } else if (applicant.enrolmentSchoolYear !== startYear) {
        return false;
      }
    }
    
    // School year on entry filter
    if (applicantsSchoolYear) {
      const enrolmentSchoolYear = applicant.enrolmentSchoolYear;
      // Extract the start year from the academic year string (e.g., "2024/2025" -> 2024)
      const filterYear = parseInt(applicantsSchoolYear.split('/')[0]);
      
      // For applicants: Compare with the numeric enrolmentSchoolYear
      // For current pupils: Check if enrolmentDate falls within the academic year
      if (applicant.isCurrentPupil && applicant.enrolmentDate) {
        const enrolDate = new Date(applicant.enrolmentDate);
        if (!isNaN(enrolDate.getTime())) {
          // Check if enrollment date falls within the selected academic year
          // e.g., 2024/2025 means Sept 1, 2024 to Aug 30, 2025
          const academicYearStart = new Date(`${filterYear}-09-01T00:00:00`).getTime();
          let academicYearEnd;
          
          // Special handling for 2024/2025
          if (filterYear === 2024) {
            academicYearEnd = new Date('2025-08-30T23:59:59').getTime();
          } else {
            academicYearEnd = new Date(`${filterYear + 1}-08-31T23:59:59`).getTime();
          }
          
          const enrolDateTime = enrolDate.getTime();
          if (enrolDateTime < academicYearStart || enrolDateTime > academicYearEnd) {
            return false;
          }
        }
      } else {
        // For applicants, compare enrolmentSchoolYear
        if (enrolmentSchoolYear !== filterYear) return false;
      }
    }
    
    // School filter - handle overlapping NC codes between schools
    if (applicantsSchool) {
      if (applicantsSchool === 'Girls School') {
        // Girls School: ONLY show entries from Girls School API (school field must match)
        if (applicant.school !== 'Girls School') return false;
      } else {
        // Other schools: Filter by year group ranges
        // Exclude Girls School entries to avoid NC code overlap
        if (applicant.school === 'Girls School') return false;
        
        // Validate year group ranges for the selected school
        const yearGroupNum = getYearGroupNumber(applicant.enrolmentSchoolYearGroup);
        if (yearGroupNum === null) return false;
        
        switch (applicantsSchool) {
          case 'Bani Hajer':
            if (yearGroupNum < -1 || yearGroupNum > 6) return false;
            break;
          case 'MOQ Senior':
            if (yearGroupNum < 7 || yearGroupNum > 13) return false;
            break;
          case 'MOQ Prep':
            if (yearGroupNum < 15 || yearGroupNum > 22) return false;
            break;
          case 'Qetaf':
            if (yearGroupNum < 26 || yearGroupNum > 33) return false;
            break;
          case 'Boys School':
            if (yearGroupNum < 34 || yearGroupNum > 41) return false;
            break;
          default:
            break;
        }
      }
    }
    
    // If date filters are empty, show all (that passed school filter)
    if (!applicantsYear && !applicantsMonth) return true;
    
    // Use registeredDate for applicants, enrolmentDate for current pupils
    const dateField = applicant.isCurrentPupil 
      ? applicant.enrolmentDate 
      : (applicant.registeredDate || applicant.enquiryDate || applicant.enrolmentDate);
    
    if (!dateField) {
      return false; // Don't show applicants without dates when filters are active
    }
    
    const appDate = new Date(dateField);
    
    // Check if date is valid
    if (isNaN(appDate.getTime())) {
      return false;
    }
    
    // Academic year logic with special handling for 2024 and 2025
    // 2024: September 1, 2024 to August 30, 2025
    // 2025: August 31, 2025 to August 31, 2026
    // Others: September 1 to August 31 of next year
    const yearMatch = applicantsYear ? (() => {
      const selectedYear = parseInt(applicantsYear);
      const appDateTime = appDate.getTime();
      
      // Special handling for 2024
      if (selectedYear === 2024) {
        const start = new Date('2024-09-01T00:00:00').getTime();
        const end = new Date('2025-08-30T23:59:59').getTime();
        return appDateTime >= start && appDateTime <= end;
      }
      
      // Special handling for 2025
      if (selectedYear === 2025) {
        const start = new Date('2025-08-31T00:00:00').getTime();
        const end = new Date('2026-08-31T23:59:59').getTime();
        return appDateTime >= start && appDateTime <= end;
      }
      
      // Standard logic for other years: Sept 1 to Aug 31 of next year
      const month = appDate.getMonth() + 1; // 1-12
      const year = appDate.getFullYear();
      
      if (month >= 9 && month <= 12) {
        return year === selectedYear;
      } else if (month >= 1 && month <= 8) {
        return year === selectedYear + 1;
      }
      return false;
    })() : true;
    
    const monthMatch = applicantsMonth ? appDate.getMonth() + 1 === parseInt(applicantsMonth) : true;
    
    return yearMatch && monthMatch;
  });

  // Gender distribution for donut chart
  const genderData = filteredApplicants.reduce((acc, applicant) => {
    let gender = applicant.gender || 'Unknown';
    
    // Normalize gender values to match color keys
    if (gender === 'M' || gender === 'Male' || gender === 'male') {
      gender = 'Male';
    } else if (gender === 'F' || gender === 'Female' || gender === 'female') {
      gender = 'Female';
    } else {
      gender = 'Unknown';
    }
    
    acc[gender] = (acc[gender] || 0) + 1;
    return acc;
  }, {});

  const genderChartData = Object.entries(genderData).map(([gender, count]) => ({
    name: gender,
    value: count
  }));

  const GENDER_COLORS = {
    'Male': '#3b82f6',     // Blue
    'Female': '#ec4899',   // Pink
    'Unknown': '#9ca3af'   // Gray
  };

  // Nationality distribution for horizontal bar chart
  const nationalityData = {};
  
  // Create a map of synced students by schoolId for quick lookup
  const syncedStudentsMap = new Map();
  if (students && students.length > 0) {
    students.forEach(student => {
      if (student.school_id) {
        syncedStudentsMap.set(student.school_id, student);
      }
    });
  }
  
  // Process applicants
  filteredApplicants.forEach(applicant => {
    let nationality;
    
    // If this is a current pupil, try to get nationality from synced students first
    if (applicant.isCurrentPupil && applicant.schoolId && syncedStudentsMap.has(applicant.schoolId)) {
      const syncedStudent = syncedStudentsMap.get(applicant.schoolId);
      nationality = getNationality(syncedStudent);
    } else {
      // For non-current pupils or if no match found, use applicant's own data
      nationality = getNationality(applicant);
    }
    
    nationalityData[nationality] = (nationalityData[nationality] || 0) + 1;
  });

  const nationalityChartData = Object.entries(nationalityData)
    .map(([nationality, count]) => ({
      nationality,
      count
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 20); // Top 20 nationalities

  // Year group name mapping
  const yearGroupNameMapping = {
    '-1': 'PS BH',
    '0': 'R BH',
    '1': 'Y1 BH',
    '2': 'Y2 BH',
    '3': 'Y3 BH',
    '4': 'Y4 BH',
    '5': 'Y5 BH',
    '6': 'Y6 BH',
    '7': 'Y7 MOQ',
    '8': 'Y8 MOQ',
    '9': 'Y9 MOQ',
    '10': 'Y10 MOQ',
    '11': 'Y11 MOQ',
    '12': 'Y12 MOQ',
    '13': 'Y13 MOQ',
    '15': 'PS MOQ',
    '16': 'R MOQ',
    '17': 'Y1 MOQ',
    '18': 'Y2 MOQ',
    '19': 'Y3 MOQ',
    '20': 'Y4 MOQ',
    '21': 'Y5 MOQ',
    '22': 'Y6 MOQ',
    '26': 'R Q',
    '27': 'Y1Q',
    '28': 'Y2Q',
    '29': 'Y3Q',
    '30': 'Y4Q',
    '31': 'Y5Q',
    '32': 'Y6Q',
    '33': 'Y7Q',
    '34': 'PBS',
    '35': 'RBS',
    '36': 'Y1BS',
    '37': 'Y2BS',
    '38': 'Y3BS',
    '39': 'Y4BS',
    '40': 'Y5BS',
    '41': 'Y6BS'
  };

  // Girls School year group mapping
  const girlsSchoolMapping = {
    '-1': 'PSGS',
    '0': 'RGS',
    '1': 'Y1GS',
    '2': 'Y2GS',
    '3': 'Y3GS',
    '4': 'Y4GS',
    '5': 'Y5GS',
    '6': 'Y6GS',
    '7': 'Y7GS',
    '8': 'Y8GS',
    '9': 'Y9GS',
    '10': 'Y10GS',
    '11': 'Y11GS',
    '12': 'Y12GS'
  };

  // Year on Entry distribution for column chart
  const yearOnEntryData = filteredApplicants.reduce((acc, applicant) => {
    // For applicants use enrolmentSchoolYear, for current pupils extract year from enrolmentDate
    let yearOnEntry;
    
    if (applicant.isCurrentPupil) {
      if (applicant.enrolmentDate) {
        // Extract year from enrolmentDate (e.g., "2020-09-01" -> 2020)
        const enrolmentDate = new Date(applicant.enrolmentDate);
        if (!isNaN(enrolmentDate.getTime())) {
          const month = enrolmentDate.getMonth(); // 0-11
          const year = enrolmentDate.getFullYear();
          // If enrolled between September-December, it's that academic year
          // If enrolled between January-August, it's the previous academic year
          yearOnEntry = month >= 8 ? year : year - 1; // 8 = September (0-indexed)
        } else {
          yearOnEntry = 'Not Assigned';
        }
      } else {
        yearOnEntry = 'Not Assigned';
      }
    } else {
      yearOnEntry = applicant.enrolmentSchoolYear || 'Not Assigned';
    }
    
    // Format as academic year if it's a number (e.g., 2024 -> "2024/2025")
    let displayName;
    if (typeof yearOnEntry === 'number' || !isNaN(Number(yearOnEntry))) {
      const year = Number(yearOnEntry);
      displayName = `${year}/${year + 1}`;
    } else {
      displayName = yearOnEntry;
    }
    
    acc[displayName] = (acc[displayName] || 0) + 1;
    return acc;
  }, {});

  const yearGroupChartData = Object.entries(yearOnEntryData)
    .map(([yearGroup, count]) => ({
      yearGroup,
      count
    }))
    .sort((a, b) => {
      // Sort by year
      const getYear = (displayName) => {
        if (displayName === 'Not Assigned') return 9999;
        const match = displayName.match(/^(\d{4})/);
        return match ? parseInt(match[1]) : 9999;
      };
      
      const aYear = getYear(a.yearGroup);
      const bYear = getYear(b.yearGroup);
      if (aYear !== bYear) return aYear - bYear;
      return a.yearGroup.localeCompare(b.yearGroup);
    });

  // Admission status distribution for column chart
  const admissionStatusData = filteredApplicants.reduce((acc, applicant) => {
    const status = applicant.admissionStatus || 'Unknown';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  // Function to get color for admission status
  const getAdmissionStatusColor = (status) => {
    if (status.startsWith('7.1')) return '#10b981'; // Green
    if (['3.3', '2.1', '3.1', '3.2', '3.4'].some(code => status.startsWith(code))) return '#ef4444'; // Red
    if (status.startsWith('1.1')) return '#3b82f6'; // Blue
    if (status.startsWith('6.1')) return '#3b82f6'; // Blue
    if (status.startsWith('4.1')) return '#800080'; // Pink
    return '#f59e0b'; // Default orange
  };

  const admissionStatusChartData = Object.entries(admissionStatusData)
    .map(([status, count]) => ({
      status,
      count,
      color: getAdmissionStatusColor(status)
    }))
    .sort((a, b) => b.count - a.count);

  // Month distribution for horizontal bar chart
  const monthData = filteredApplicants.reduce((acc, applicant) => {
    const dateField = applicant.isCurrentPupil ? applicant.enrolmentDate : applicant.registeredDate;
    if (!dateField) return acc;
    
    const date = new Date(dateField);
    if (isNaN(date.getTime())) return acc;
    
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                        'July', 'August', 'September', 'October', 'November', 'December'];
    const monthName = monthNames[date.getMonth()];
    
    acc[monthName] = (acc[monthName] || 0) + 1;
    return acc;
  }, {});

  const monthChartData = Object.entries(monthData)
    .map(([month, count]) => ({
      month,
      count
    }))
    .sort((a, b) => {
      const monthOrder = ['January', 'February', 'March', 'April', 'May', 'June', 
                          'July', 'August', 'September', 'October', 'November', 'December'];
      return monthOrder.indexOf(a.month) - monthOrder.indexOf(b.month);
    });

  const years = [2020, 2021, 2022, 2023, 2024, 2025, 2026];
  const months = [
    { value: '', label: 'All Months' },
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex flex-wrap gap-4 items-center">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">School</label>
            <select
              value={applicantsSchool}
              onChange={(e) => setApplicantsSchool(e.target.value)}
              className="border rounded px-3 py-2"
            >
              <option value="">All Schools</option>
              <option value="Bani Hajer">Bani Hajer</option>
              <option value="Boys School">Boys School</option>
              <option value="Qetaf">Qetaf</option>
              <option value="MOQ Senior">MOQ Senior</option>
              <option value="MOQ Prep">MOQ Prep</option>
              <option value="Girls School">Girls School</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
            <select
              value={applicantsYear}
              onChange={(e) => setApplicantsYear(e.target.value)}
              className="border rounded px-3 py-2"
            >
              <option value="">All Years</option>
              {years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
            <select
              value={applicantsMonth}
              onChange={(e) => setApplicantsMonth(e.target.value)}
              className="border rounded px-3 py-2"
            >
              {months.map(month => (
                <option key={month.value} value={month.value}>{month.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Admission Status</label>
            <select
              value={applicantsAdmissionStatus}
              onChange={(e) => setApplicantsAdmissionStatus(e.target.value)}
              className="border rounded px-3 py-2"
            >
              <option value="">All Statuses</option>
              {[...new Set(applicantsData.map(a => a.admissionStatus).filter(Boolean))]
                .sort()
                .map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Starting Year</label>
            <select
              value={applicantsStartingYear}
              onChange={(e) => setApplicantsStartingYear(e.target.value)}
              className="border rounded px-3 py-2"
            >
              <option value="">All Starting Years</option>
              {years.map(year => (
                <option key={year} value={year}>{year}/{year + 1}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Total Count */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="text-center">
          <p className="text-gray-600 text-sm font-medium">Total Applicants</p>
          <p className="text-4xl font-bold text-green-600 mt-2">{filteredApplicants.length}</p>
        </div>
      </div>

      {/* Top Row - 2 Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Admission Status Chart - LEFT */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4">Admission Status</h3>
          {admissionStatusChartData.length > 0 ? (
            <div className="overflow-y-auto" style={{ maxHeight: '400px' }}>
              <ResponsiveContainer width="100%" height={Math.max(admissionStatusChartData.length * 30, 400)}>
                <BarChart
                  data={admissionStatusChartData}
                  layout="vertical"
                  margin={{ top: 5, right: 60, left: 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" style={{ fontSize: '12px' }} />
                  <YAxis dataKey="status" type="category" width={150} style={{ fontSize: '12px' }} />
                  <Tooltip contentStyle={{ fontSize: '12px' }} />
                  <Bar dataKey="count" label={{ position: 'right', fontSize: 11 }}>
                    {admissionStatusChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">No data available</div>
          )}
        </div>

        {/* Year on Entry Chart - RIGHT (Horizontal) */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4">Year on Entry Distribution</h3>
          {yearGroupChartData.length > 0 ? (
            <div className="overflow-y-auto" style={{ maxHeight: '400px' }}>
              <ResponsiveContainer width="100%" height={Math.max(yearGroupChartData.length * 35, 400)}>
                <BarChart
                  data={yearGroupChartData}
                  layout="vertical"
                  margin={{ top: 5, right: 60, left: 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" style={{ fontSize: '12px' }} />
                  <YAxis dataKey="yearGroup" type="category" width={80} style={{ fontSize: '12px' }} />
                  <Tooltip contentStyle={{ fontSize: '12px' }} />
                  <Bar dataKey="count" fill="#8b5cf6" label={{ position: 'right', fontSize: 11 }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">No data available</div>
          )}
        </div>
      </div>

      {/* Second Row - Gender and Nationality Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gender Donut Chart - LEFT */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4">Gender Distribution</h3>
          {genderChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <PieChart>
                <Pie
                  data={genderChartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {genderChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={GENDER_COLORS[entry.name] || '#9ca3af'} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center text-gray-500 py-8">No data available</div>
          )}
        </div>

        {/* Nationality Horizontal Bar Chart - RIGHT */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4">Top Nationalities</h3>
          {nationalityChartData.length > 0 ? (
            <div className="overflow-y-auto" style={{ maxHeight: '400px' }}>
              <ResponsiveContainer width="100%" height={Math.max(nationalityChartData.length * 35, 400)}>
                <BarChart
                  data={nationalityChartData}
                  layout="vertical"
                  margin={{ top: 5, right: 60, left: 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="nationality" type="category" width={100} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#10b981" label={{ position: 'right' }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">No data available</div>
          )}
        </div>
      </div>

      {/* Month Distribution Chart - Full Width at Bottom */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Month Distribution</h3>
        {monthChartData.length > 0 ? (
          <div className="overflow-x-auto">
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={monthChartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="month" 
                  angle={-45} 
                  textAnchor="end" 
                  height={80}
                  style={{ fontSize: '12px' }}
                />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#06b6d4" label={{ position: 'top' }} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="text-center text-gray-500 py-8">No data available</div>
        )}
      </div>

      {/* Applicants Table with Timeline */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Applicants List ({filteredApplicants.length})</h3>
        
        {filteredApplicants.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gender</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Year Group</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timeline</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredApplicants
                    .slice((currentPage - 1) * 20, currentPage * 20)
                    .map((applicant, index) => {
                      const name = applicant.fullName || applicant.full_name || applicant.name || 'Unknown';
                      const yearGroup = applicant.enrolmentSchoolYearGroup || applicant.yearGroup || 'N/A';
                      const status = applicant.admissionStatus || 'Unknown';
                      const gender = applicant.gender === 'M' || applicant.gender === 'Male' ? 'Male' : applicant.gender === 'F' || applicant.gender === 'Female' ? 'Female' : applicant.gender || 'N/A';
                      
                      // Timeline dates
                      const enquiryDate = applicant.enquiryDate;
                      const registeredDate = applicant.registeredDate;
                      const enrolmentDate = applicant.enrolmentDate;
                      const isAccepted = status.startsWith('7.1');
                      
                      const formatDate = (dateStr) => {
                        if (!dateStr) return null;
                        const d = new Date(dateStr);
                        if (isNaN(d.getTime())) return null;
                        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                      };

                      return (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-500">{(currentPage - 1) * 20 + index + 1}</td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{name}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              gender === 'Male' ? 'bg-blue-100 text-blue-700' : gender === 'Female' ? 'bg-pink-100 text-pink-700' : 'bg-gray-100 text-gray-700'
                            }`}>
                              {gender}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">{yearGroupNameMapping[String(yearGroup)] || `Year ${yearGroup}`}</td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              isAccepted ? 'bg-green-100 text-green-700' : 
                              status.startsWith('1.1') ? 'bg-blue-100 text-blue-700' :
                              status.startsWith('3.') ? 'bg-red-100 text-red-700' :
                              status.startsWith('6.1') ? 'bg-blue-100 text-blue-700' :
                              status.startsWith('4.1') ? 'bg-purple-100 text-purple-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {status}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1 min-w-[320px]">
                              {/* Enquiry */}
                              <div className="flex flex-col items-center" title={enquiryDate ? `Enquiry: ${formatDate(enquiryDate)}` : 'No enquiry date'}>
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${enquiryDate ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-400'}`}>
                                  📩
                                </div>
                                <span className="text-[10px] text-gray-500 mt-0.5">{formatDate(enquiryDate) || '—'}</span>
                              </div>
                              
                              {/* Line */}
                              <div className={`flex-1 h-0.5 ${registeredDate ? 'bg-green-400' : 'bg-gray-200'}`}></div>
                              
                              {/* Application Received */}
                              <div className="flex flex-col items-center" title={registeredDate ? `Received: ${formatDate(registeredDate)}` : 'No received date'}>
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${registeredDate ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-400'}`}>
                                  📋
                                </div>
                                <span className="text-[10px] text-gray-500 mt-0.5">{formatDate(registeredDate) || '—'}</span>
                                <span className="text-[9px] text-gray-400">Received</span>
                              </div>
                              
                              {/* Line */}
                              <div className={`flex-1 h-0.5 ${isAccepted && enrolmentDate ? 'bg-emerald-400' : 'bg-gray-200'}`}></div>
                              
                              {/* Approved / Enrolled */}
                              <div className="flex flex-col items-center" title={isAccepted && enrolmentDate ? `Approved: ${formatDate(enrolmentDate)}` : 'Not yet approved'}>
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${isAccepted && enrolmentDate ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-400'}`}>
                                  ✅
                                </div>
                                <span className="text-[10px] text-gray-500 mt-0.5">{isAccepted && enrolmentDate ? formatDate(enrolmentDate) : '—'}</span>
                                <span className="text-[9px] text-gray-400">Approved</span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {filteredApplicants.length > 20 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <div className="text-sm text-gray-600">
                  Showing {(currentPage - 1) * 20 + 1} - {Math.min(currentPage * 20, filteredApplicants.length)} of {filteredApplicants.length}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 rounded border text-sm disabled:opacity-40 hover:bg-gray-50"
                  >
                    ← Previous
                  </button>
                  {Array.from({ length: Math.min(5, Math.ceil(filteredApplicants.length / 20)) }, (_, i) => {
                    const totalPages = Math.ceil(filteredApplicants.length / 20);
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 rounded border text-sm ${currentPage === pageNum ? 'bg-blue-600 text-white' : 'hover:bg-gray-50'}`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredApplicants.length / 20), p + 1))}
                    disabled={currentPage >= Math.ceil(filteredApplicants.length / 20)}
                    className="px-3 py-1 rounded border text-sm disabled:opacity-40 hover:bg-gray-50"
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center text-gray-500 py-8">No applicants match the selected filters</div>
        )}
      </div>
    </div>
  );
};

// Leavers Report Component
const LeaversReport = ({ 
  leaversData,
  isLoadingLeavers, 
  leaversSchool,
  setLeaversSchool,
  leaversYear,
  setLeaversYear,
  leaversMonth,
  setLeaversMonth,
  fetchLeavers
}) => {
  const [currentPage, setCurrentPage] = useState(1);

  // Helper function to get year group as number
  const getYearGroupNumber = (yearGroup) => {
    if (!yearGroup) return null;
    const str = String(yearGroup);
    const match = str.match(/-?\d+/);
    return match ? parseInt(match[0]) : null;
  };

  // Helper to get year group from leavingYearGroup or enrolmentSchoolYearGroup
  const getLeaverYearGroup = (leaver) => {
    return leaver.leavingYearGroup || leaver.enrolmentSchoolYearGroup;
  };

  // Filter leavers by school, year and month
  console.log('🔍 Filter values - School:', leaversSchool, '| Year:', leaversYear, '| Month:', leaversMonth);
  
  let debugCount = 0;
  const filteredLeavers = leaversData.filter(leaver => {
    // School filter
    if (leaversSchool) {
      const yearGroupNum = getYearGroupNumber(getLeaverYearGroup(leaver));
      
      // Check if it's from Girls School API
      if (leaver.school === 'Girls School') {
        if (leaversSchool !== 'Girls School') return false;
      } else {
        // For Main School leavers, filter by year group ranges
        if (yearGroupNum !== null) {
          let schoolMatch = false;
          switch (leaversSchool) {
            case 'Bani Hajer':
              schoolMatch = yearGroupNum >= -1 && yearGroupNum <= 6;
              break;
            case 'MOQ Senior':
              schoolMatch = yearGroupNum >= 7 && yearGroupNum <= 13;
              break;
            case 'MOQ Prep':
              schoolMatch = yearGroupNum >= 15 && yearGroupNum <= 22;
              break;
            case 'Qetaf':
              schoolMatch = yearGroupNum >= 26 && yearGroupNum <= 33;
              break;
            case 'Boys School':
              schoolMatch = yearGroupNum >= 34 && yearGroupNum <= 41;
              break;
          }
          if (!schoolMatch) return false;
        } else {
          // If no valid year group and not Girls School, exclude
          return false;
        }
      }
    }
    
    // Date filters (year and month) - Use leavingYear for year filter, leavingDate for month filter
    // If no year selected and no month selected, show all
    if (!leaversYear && !leaversMonth) {
      return true;
    }
    
    // Year filter: Check leavingYear directly
    let yearMatch = true;
    if (leaversYear) {
      const selectedYear = parseInt(leaversYear);
      const recordLeavingYear = leaver.leavingYear;
      
      yearMatch = recordLeavingYear === selectedYear;
      
      // Debug first 5 records
      if (debugCount < 5) {
        console.log(`🔍 Record ${debugCount + 1}: leavingYear=${leaver.leavingYear}, leavingDate=${leaver.leavingDate} -> ${yearMatch ? '✓' : '✗'} (Filter: ${selectedYear})`);
        debugCount++;
      }
    }
    
    // Month filter: Use leavingDate
    let monthMatch = true;
    if (leaversMonth) {
      const dateField = leaver.leavingDate || leaver.lastUpdated || leaver.enrolmentDate;
      if (dateField) {
        const leaverDate = new Date(dateField);
        if (!isNaN(leaverDate.getTime())) {
          monthMatch = leaverDate.getMonth() + 1 === parseInt(leaversMonth);
        } else {
          monthMatch = false;
        }
      } else {
        monthMatch = false;
      }
    }
    
    return yearMatch && monthMatch;
  });

  // Generate year options (last 10 years)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - i);

  // Gender chart data
  const genderData = filteredLeavers.reduce((acc, leaver) => {
    let gender = leaver.gender || 'Unknown';
    // Map gender codes to full names
    if (gender === 'M') gender = 'Male';
    else if (gender === 'F') gender = 'Female';
    else if (typeof gender === 'string') {
      // Normalize gender values to match color mapping
      gender = gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase();
    }
    acc[gender] = (acc[gender] || 0) + 1;
    return acc;
  }, {});

  const genderChartData = Object.entries(genderData).map(([name, value]) => ({ name, value }));

  const GENDER_COLORS = {
    'Male': '#3b82f6',      // Blue
    'Female': '#ec4899',    // Pink
    'Unknown': '#9ca3af'    // Gray
  };

  // Month distribution
  const monthData = filteredLeavers.reduce((acc, leaver) => {
    const dateField = leaver.leavingDate || leaver.lastUpdated || leaver.enrolmentDate;
    if (dateField) {
      const date = new Date(dateField);
      if (!isNaN(date.getTime())) {
        const month = date.toLocaleString('default', { month: 'short' });
        acc[month] = (acc[month] || 0) + 1;
      }
    }
    return acc;
  }, {});

  const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthChartData = monthOrder
    .filter(month => monthData[month])
    .map(month => ({ month, count: monthData[month] }));

  // Year Group mapping with school abbreviations (matching Applicants tab format)
  const yearGroupNameMapping = {
    '-1': 'PS BH',
    '0': 'R BH',
    '1': 'Y1 BH',
    '2': 'Y2 BH',
    '3': 'Y3 BH',
    '4': 'Y4 BH',
    '5': 'Y5 BH',
    '6': 'Y6 BH',
    '7': 'Y7 MOQ',
    '8': 'Y8 MOQ',
    '9': 'Y9 MOQ',
    '10': 'Y10 MOQ',
    '11': 'Y11 MOQ',
    '12': 'Y12 MOQ',
    '13': 'Y13 MOQ',
    '15': 'PS MOQ',
    '16': 'R MOQ',
    '17': 'Y1 MOQ',
    '18': 'Y2 MOQ',
    '19': 'Y3 MOQ',
    '20': 'Y4 MOQ',
    '21': 'Y5 MOQ',
    '22': 'Y6 MOQ',
    '26': 'R Q',
    '27': 'Y1Q',
    '28': 'Y2Q',
    '29': 'Y3Q',
    '30': 'Y4Q',
    '31': 'Y5Q',
    '32': 'Y6Q',
    '33': 'Y7Q',
    '34': 'PBS',
    '35': 'RBS',
    '36': 'Y1BS',
    '37': 'Y2BS',
    '38': 'Y3BS',
    '39': 'Y4BS',
    '40': 'Y5BS',
    '41': 'Y6BS'
  };

  const girlsSchoolMapping = {
    '-1': 'PSGS',
    '0': 'RGS',
    '1': 'Y1GS',
    '2': 'Y2GS',
    '3': 'Y3GS',
    '4': 'Y4GS',
    '5': 'Y5GS',
    '6': 'Y6GS',
    '7': 'Y7GS',
    '8': 'Y8GS',
    '9': 'Y9GS',
    '10': 'Y10GS',
    '11': 'Y11GS',
    '12': 'Y12GS'
  };

  // Year group distribution
  const yearGroupData = filteredLeavers.reduce((acc, leaver) => {
    const yearGroup = getLeaverYearGroup(leaver) || 'Not Assigned';
    
    let displayName;
    if (leaver.school === 'Girls School') {
      displayName = girlsSchoolMapping[String(yearGroup)] || yearGroup;
    } else {
      displayName = yearGroupNameMapping[String(yearGroup)] || yearGroup;
    }
    
    acc[displayName] = (acc[displayName] || 0) + 1;
    return acc;
  }, {});
  
  console.log(`📊 Filtered leavers count: ${filteredLeavers.length}`);
  console.log(`📊 Year group breakdown:`, JSON.stringify(yearGroupData, null, 2));
  
  // Debug: Show stats on missing year groups
  const withYearGroup = filteredLeavers.filter(l => getLeaverYearGroup(l) !== null && getLeaverYearGroup(l) !== undefined);
  const withoutYearGroup = filteredLeavers.filter(l => !getLeaverYearGroup(l));
  console.log(`📊 Records with year group: ${withYearGroup.length} (${Math.round(withYearGroup.length/filteredLeavers.length*100)}%)`);
  console.log(`📊 Records without year group: ${withoutYearGroup.length} (${Math.round(withoutYearGroup.length/filteredLeavers.length*100)}%)`);
  if (withoutYearGroup.length > 0) {
    console.log(`📊 Sample record without year group:`, {
      fullName: withoutYearGroup[0].fullName,
      leavingDate: withoutYearGroup[0].leavingDate,
      leavingYear: withoutYearGroup[0].leavingYear,
      enrolmentDate: withoutYearGroup[0].enrolmentDate,
      leavingYearGroup: withoutYearGroup[0].leavingYearGroup,
      enrolmentSchoolYearGroup: withoutYearGroup[0].enrolmentSchoolYearGroup
    });
  }
  
  // Debug: Show sample of records with year group -1
  const yearNeg1Records = filteredLeavers.filter(l => {
    const yg = getLeaverYearGroup(l);
    return yg === -1 || yg === '-1';
  });
  console.log(`📊 Year -1 records count: ${yearNeg1Records.length}`);
  if (yearNeg1Records.length > 0) {
    console.log(`📊 Sample year -1 record:`, yearNeg1Records[0]);
  }
  
  // Log a full sample record to see available fields
  if (filteredLeavers.length > 0) {
    console.log(`🔍 Full sample leaver record:`, filteredLeavers[0]);
  }

  const yearGroupChartData = Object.entries(yearGroupData)
    .map(([yearGroup, count]) => ({ yearGroup, count }))
    .sort((a, b) => {
      const getOriginalNum = (displayName) => {
        const reverseMapping = Object.entries(yearGroupNameMapping).find(([num, name]) => name === displayName);
        if (reverseMapping) return parseInt(reverseMapping[0]);
        const num = parseInt(displayName);
        return isNaN(num) ? 999 : num;
      };
      
      const aNum = getOriginalNum(a.yearGroup);
      const bNum = getOriginalNum(b.yearGroup);
      if (aNum !== bNum) return aNum - bNum;
      return a.yearGroup.localeCompare(b.yearGroup);
    });

  // Nationality distribution (top 10)
  const nationalityData = filteredLeavers.reduce((acc, leaver) => {
    const nationality = leaver.nationalities || leaver.nationality || 'Unknown';
    acc[nationality] = (acc[nationality] || 0) + 1;
    return acc;
  }, {});

  const nationalityChartData = Object.entries(nationalityData)
    .map(([nationality, count]) => ({ nationality, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  // Leaving reason distribution
  const leavingReasonData = filteredLeavers.reduce((acc, leaver) => {
    const reason = leaver.leavingReason || 'Unknown';
    acc[reason] = (acc[reason] || 0) + 1;
    return acc;
  }, {});

  const leavingReasonChartData = Object.entries(leavingReasonData)
    .map(([reason, count]) => ({ reason, count }))
    .sort((a, b) => b.count - a.count);

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">School</label>
            <select
              value={leaversSchool}
              onChange={(e) => setLeaversSchool(e.target.value)}
              className="border rounded px-3 py-2"
            >
              <option value="">All Schools</option>
              <option value="Bani Hajer">Bani Hajer</option>
              <option value="Girls School">Girls School</option>
              <option value="MOQ Senior">MOQ Senior</option>
              <option value="MOQ Prep">MOQ Prep</option>
              <option value="Boys School">Boys School</option>
              <option value="Qetaf">Qetaf</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
            <select
              value={leaversYear}
              onChange={(e) => setLeaversYear(e.target.value)}
              className="border rounded px-3 py-2"
            >
              <option value="">All Years</option>
              {years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
            <select
              value={leaversMonth}
              onChange={(e) => setLeaversMonth(e.target.value)}
              className="border rounded px-3 py-2"
            >
              <option value="">All Months</option>
              <option value="1">January</option>
              <option value="2">February</option>
              <option value="3">March</option>
              <option value="4">April</option>
              <option value="5">May</option>
              <option value="6">June</option>
              <option value="7">July</option>
              <option value="8">August</option>
              <option value="9">September</option>
              <option value="10">October</option>
              <option value="11">November</option>
              <option value="12">December</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              onClick={fetchLeavers}
              disabled={isLoadingLeavers}
            >
              {isLoadingLeavers ? 'Loading...' : '📊 Load Data'}
            </button>
          </div>
        </div>
      </div>

      {/* Total Count */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="text-center">
          <p className="text-gray-600 text-sm font-medium">Total Leavers</p>
          <p className="text-4xl font-bold text-red-600 mt-2">{filteredLeavers.length}</p>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gender Chart */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4">Gender Distribution</h3>
          {genderChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={genderChartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {genderChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={GENDER_COLORS[entry.name] || '#9ca3af'} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center text-gray-500 py-8">No data available</div>
          )}
        </div>

        {/* Year Group Chart */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4">Year Group Distribution</h3>
          {yearGroupChartData.length > 0 ? (
            <div className="overflow-y-auto" style={{ maxHeight: '300px' }}>
              <ResponsiveContainer width="100%" height={Math.max(yearGroupChartData.length * 35, 300)}>
                <BarChart
                  data={yearGroupChartData}
                  layout="vertical"
                  margin={{ top: 5, right: 60, left: 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" style={{ fontSize: '12px' }} />
                  <YAxis dataKey="yearGroup" type="category" width={80} style={{ fontSize: '12px' }} />
                  <Tooltip contentStyle={{ fontSize: '12px' }} />
                  <Bar dataKey="count" fill="#ef4444" label={{ position: 'right', fontSize: 11 }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">No data available</div>
          )}
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Leaving Reason Chart */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4">Leaving Reasons</h3>
          {leavingReasonChartData.length > 0 ? (
            <div className="overflow-y-auto" style={{ maxHeight: '400px' }}>
              <ResponsiveContainer width="100%" height={Math.max(leavingReasonChartData.length * 40, 300)}>
                <BarChart
                  data={leavingReasonChartData}
                  layout="vertical"
                  margin={{ top: 5, right: 60, left: 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" style={{ fontSize: '12px' }} />
                  <YAxis dataKey="reason" type="category" width={150} style={{ fontSize: '11px' }} />
                  <Tooltip contentStyle={{ fontSize: '12px' }} />
                  <Bar dataKey="count" fill="#8b5cf6" label={{ position: 'right', fontSize: 11 }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">No data available</div>
          )}
        </div>

        {/* Nationality Chart */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4">Top Nationalities</h3>
          {nationalityChartData.length > 0 ? (
            <div className="overflow-y-auto" style={{ maxHeight: '300px' }}>
              <ResponsiveContainer width="100%" height={Math.max(nationalityChartData.length * 35, 300)}>
                <BarChart
                  data={nationalityChartData}
                  layout="vertical"
                  margin={{ top: 5, right: 60, left: 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="nationality" type="category" width={100} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#10b981" label={{ position: 'right' }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">No data available</div>
          )}
        </div>
      </div>

      {/* Month Distribution */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Month Distribution</h3>
        {monthChartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={monthChartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" style={{ fontSize: '12px' }} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#f59e0b" label={{ position: 'top' }} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center text-gray-500 py-8">No data available</div>
        )}
      </div>
    </div>
  );
};


// ── Device Registry Tab ──────────────────────────────────────────────────────
const DevicesTab = () => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [deleting, setDeleting] = useState(null);

  const fetchDevices = async () => {
    try {
      setLoading(true);
      const res = await fetch(buildApiUrl("/api/devices"));
      const data = await res.json();
      if (data.success) {
        setDevices(data.devices);
        setError(null);
      } else {
        setError(data.error || "Failed to load devices");
      }
    } catch (e) {
      setError("Cannot connect to backend.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDevices();
    const interval = setInterval(fetchDevices, 30000); // refresh every 30 s
    return () => clearInterval(interval);
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this device record?")) return;
    setDeleting(id);
    try {
      await fetch(buildApiUrl(`/api/devices/${id}`), { method: "DELETE" });
      setDevices(prev => prev.filter(d => d.id !== id));
    } catch (e) {
      alert("Delete failed");
    } finally {
      setDeleting(null);
    }
  };

  const filtered = devices.filter(d => {
    const q = search.toLowerCase();
    return (
      (d.device_name  || "").toLowerCase().includes(q) ||
      (d.school_id    || "").toLowerCase().includes(q) ||
      (d.device_ip    || "").toLowerCase().includes(q) ||
      (d.public_ip    || "").toLowerCase().includes(q) ||
      (d.mac_address  || "").toLowerCase().includes(q) ||
      (d.device_os    || "").toLowerCase().includes(q)
    );
  });

  const connected   = devices.filter(d => d.is_connected).length;
  const disconnected = devices.length - connected;

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-xl font-semibold">🖥️ Student Devices</h2>
          <p className="text-gray-500 text-sm mt-1">
            All devices running the School Device Agent.  Auto-refreshes every 30 s.
          </p>
        </div>
        <button
          onClick={fetchDevices}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm"
        >
          🔄 Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="flex gap-4 mb-4">
        <div className="bg-gray-50 border rounded-lg px-4 py-2 text-sm">
          <span className="font-semibold">{devices.length}</span> total
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2 text-sm text-green-700">
          <span className="font-semibold">{connected}</span> connected
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2 text-sm text-red-600">
          <span className="font-semibold">{disconnected}</span> offline
        </div>
      </div>

      {/* Search */}
      <input
        type="text"
        placeholder="Search by name, IP, MAC, OS…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="border rounded px-3 py-2 text-sm w-full max-w-sm mb-4"
      />

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4 text-sm">
          ⚠️ {error}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="text-center py-10">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-gray-500 text-sm">Loading devices…</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg shadow border">
          <table className="min-w-full bg-white text-sm">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th className="py-3 px-3 border-b font-semibold text-gray-700">Status</th>
                <th className="py-3 px-3 border-b font-semibold text-gray-700">Device Name</th>
                <th className="py-3 px-3 border-b font-semibold text-gray-700">School ID</th>
                <th className="py-3 px-3 border-b font-semibold text-gray-700">Device IP</th>
                <th className="py-3 px-3 border-b font-semibold text-gray-700">Public IP</th>
                <th className="py-3 px-3 border-b font-semibold text-gray-700">VPN</th>
                <th className="py-3 px-3 border-b font-semibold text-gray-700">MAC Address</th>
                <th className="py-3 px-3 border-b font-semibold text-gray-700">OS</th>
                <th className="py-3 px-3 border-b font-semibold text-gray-700">Type</th>
                <th className="py-3 px-3 border-b font-semibold text-gray-700">RAM</th>
                <th className="py-3 px-3 border-b font-semibold text-gray-700">Last Seen</th>
                <th className="py-3 px-3 border-b font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan="12" className="py-8 text-center text-gray-400">
                    {devices.length === 0
                      ? "No devices registered yet.  Install the School Device Agent on student computers."
                      : "No devices match your search."}
                  </td>
                </tr>
              ) : (
                filtered.map(d => {
                  const lastSeenDate = d.last_seen ? new Date(d.last_seen) : null;
                  const lastSeenStr = lastSeenDate
                    ? lastSeenDate.toLocaleString()
                    : "—";
                  return (
                    <tr key={d.id} className="hover:bg-gray-50 transition-colors">
                      {/* Status */}
                      <td className="py-2 px-3 border-b">
                        <span
                          className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${
                            d.is_connected
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-100 text-gray-500"
                          }`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${
                              d.is_connected ? "bg-green-500 animate-pulse" : "bg-gray-400"
                            }`}
                          />
                          {d.is_connected ? "Connected" : "Offline"}
                        </span>
                      </td>
                      <td className="py-2 px-3 border-b font-medium">{d.device_name || "—"}</td>
                      <td className="py-2 px-3 border-b text-gray-600">{d.school_id}</td>
                      <td className="py-2 px-3 border-b font-mono text-blue-700">{d.device_ip || "—"}</td>
                      <td className="py-2 px-3 border-b font-mono text-purple-700">{d.public_ip || "—"}</td>
                      <td className="py-2 px-3 border-b">
                        {d.is_vpn
                          ? <span className="inline-flex items-center gap-1 bg-orange-100 text-orange-700 text-xs font-medium px-2 py-0.5 rounded-full">🔒 VPN</span>
                          : <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-400 text-xs px-2 py-0.5 rounded-full">—</span>}
                      </td>
                      <td className="py-2 px-3 border-b font-mono text-xs text-gray-500">{d.mac_address}</td>
                      <td className="py-2 px-3 border-b">
                        {d.device_os ? `${d.device_os}` : "—"}
                        {d.device_os_version ? (
                          <span className="block text-xs text-gray-400">{d.device_os_version.substring(0, 40)}</span>
                        ) : null}
                      </td>
                      <td className="py-2 px-3 border-b text-gray-600">{d.device_type || "—"}</td>
                      <td className="py-2 px-3 border-b text-gray-600">{d.ram_gb ? `${d.ram_gb} GB` : "—"}</td>
                      <td className="py-2 px-3 border-b text-xs text-gray-500">{lastSeenStr}</td>
                      <td className="py-2 px-3 border-b">
                        <button
                          onClick={() => handleDelete(d.id)}
                          disabled={deleting === d.id}
                          className="text-red-500 hover:text-red-700 text-xs disabled:opacity-50"
                        >
                          {deleting === d.id ? "…" : "🗑 Remove"}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Install instructions */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
        <p className="font-semibold mb-2">📦 How to register student devices</p>
        <ol className="list-decimal list-inside space-y-1">
          <li>Download <strong>SchoolDeviceAgent.exe</strong> and <strong>agent_config.ini</strong> from the <code>device-agent/</code> folder.</li>
          <li>Edit <code>agent_config.ini</code> — set <code>school_id</code> and <code>backend_url</code>.</li>
          <li>Copy both files to the student computer and run <strong>SchoolDeviceAgent.exe</strong>.</li>
          <li>Run <strong>install_autostart.ps1</strong> (as Administrator) so it starts automatically on login.</li>
        </ol>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────────
// StudentReportsManager — upload PDFs per student, list & delete
// ──────────────────────────────────────────────────────────────────────────────
function StudentReportsManager({ students }) {
  const [uploadTab, setUploadTab] = React.useState('bulk'); // 'single' | 'bulk'
  const [reports, setReports] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [filterSchoolId, setFilterSchoolId] = React.useState('');
  const [msg, setMsg] = React.useState(null);

  // Single upload state
  const [sFile, setSFile] = React.useState(null);
  const [sSchoolId, setSSchoolId] = React.useState('');
  const [sLabel, setSLabel] = React.useState('');
  const [sYear, setSYear] = React.useState('');
  const [sType, setSType] = React.useState('');
  const [sUploading, setSUploading] = React.useState(false);

  // Bulk upload state
  const [bulkFiles, setBulkFiles] = React.useState([]); // [{file, schoolId, label, status:'pending'|'uploading'|'done'|'error', errMsg}]
  const [bulkYear, setBulkYear] = React.useState('');
  const [bulkType, setBulkType] = React.useState('');
  const [bulkUploading, setBulkUploading] = React.useState(false);
  const [bulkDone, setBulkDone] = React.useState(0);

  const currentYear = new Date().getFullYear();
  const yearOptions = [
    `${currentYear - 1}/${currentYear}`,
    `${currentYear}/${currentYear + 1}`,
    `${currentYear + 1}/${currentYear + 2}`,
  ];
  const reportTypeOptions = [
    { value: 'baseline', label: 'Baseline Report' },
    { value: 'midyear', label: 'Mid-Year Report' },
    { value: 'endofyear', label: 'End of Year Report' },
  ];

  const loadReports = () => {
    setLoading(true);
    axios.get(buildApiUrl('/api/student-reports/all/list'))
      .then(res => { if (res.data.success) setReports(res.data.reports || []); })
      .catch(() => setMsg({ type: 'error', text: 'Failed to load reports' }))
      .finally(() => setLoading(false));
  };

  React.useEffect(() => { loadReports(); }, []);

  // ── Single upload ──
  const handleSingleFileChange = (e) => {
    const f = e.target.files[0];
    setSFile(f);
    if (f) {
      const nameNoExt = f.name.replace(/\.pdf$/i, '');
      if (!sSchoolId) setSSchoolId(nameNoExt.split('_')[0]);
      if (!sLabel) setSLabel(nameNoExt);
    }
  };

  const handleSingleUpload = async () => {
    if (!sFile) { setMsg({ type: 'error', text: 'Please select a PDF file' }); return; }
    if (!sYear) { setMsg({ type: 'error', text: 'Please select an academic year' }); return; }
    if (!sType) { setMsg({ type: 'error', text: 'Please select a report type' }); return; }
    setSUploading(true); setMsg(null);
    const form = new FormData();
    form.append('file', sFile);
    if (sSchoolId.trim()) form.append('school_id', sSchoolId.trim());
    if (sLabel.trim()) form.append('label', sLabel.trim());
    form.append('year', sYear);
    form.append('report_type', sType);
    try {
      const res = await axios.post(buildApiUrl('/api/student-reports/upload'), form);
      if (res.data.success) {
        setMsg({ type: 'success', text: 'Report uploaded successfully' });
        setSFile(null); setSSchoolId(''); setSLabel(''); setSYear(''); setSType('');
        loadReports();
      } else {
        setMsg({ type: 'error', text: res.data.error || 'Upload failed' });
      }
    } catch { setMsg({ type: 'error', text: 'Upload failed' }); }
    finally { setSUploading(false); }
  };

  // ── Bulk upload ──
  const handleBulkFilesChange = (e) => {
    const files = Array.from(e.target.files);
    setBulkFiles(files.map(f => {
      const nameNoExt = f.name.replace(/\.pdf$/i, '');
      return { file: f, schoolId: nameNoExt.split('_')[0], label: nameNoExt, status: 'pending', errMsg: '' };
    }));
    setBulkDone(0);
  };

  const updateBulkFile = (idx, patch) => {
    setBulkFiles(prev => prev.map((b, i) => i === idx ? { ...b, ...patch } : b));
  };

  const handleBulkUpload = async () => {
    if (bulkFiles.length === 0) { setMsg({ type: 'error', text: 'Please select PDF files' }); return; }
    if (!bulkYear) { setMsg({ type: 'error', text: 'Please select an academic year' }); return; }
    if (!bulkType) { setMsg({ type: 'error', text: 'Please select a report type' }); return; }
    setBulkUploading(true); setMsg(null); setBulkDone(0);
    // Reset statuses
    setBulkFiles(prev => prev.map(b => ({ ...b, status: 'pending', errMsg: '' })));

    let done = 0;
    for (let i = 0; i < bulkFiles.length; i++) {
      const b = bulkFiles[i];
      updateBulkFile(i, { status: 'uploading' });
      const form = new FormData();
      form.append('file', b.file);
      if (b.schoolId.trim()) form.append('school_id', b.schoolId.trim());
      if (b.label.trim()) form.append('label', b.label.trim());
      form.append('year', bulkYear);
      form.append('report_type', bulkType);
      try {
        const res = await axios.post(buildApiUrl('/api/student-reports/upload'), form);
        if (res.data.success) {
          updateBulkFile(i, { status: 'done' });
          done++;
          setBulkDone(done);
        } else {
          updateBulkFile(i, { status: 'error', errMsg: res.data.error || 'Failed' });
        }
      } catch {
        updateBulkFile(i, { status: 'error', errMsg: 'Network error' });
      }
    }
    setBulkUploading(false);
    setMsg({ type: done > 0 ? 'success' : 'error', text: `${done} of ${bulkFiles.length} reports uploaded successfully` });
    loadReports();
  };

  // ── Shared helpers ──
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this report?')) return;
    try {
      await axios.delete(buildApiUrl(`/api/student-reports/${id}`));
      setReports(prev => prev.filter(r => r.id !== id));
    } catch { setMsg({ type: 'error', text: 'Delete failed' }); }
  };

  const getStudentName = (sid) => {
    const s = students.find(st => String(st.school_id) === String(sid));
    return s ? s.full_name : sid;
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getTypeBadge = (type) => {
    const map = {
      baseline: { label: 'Baseline', cls: 'bg-blue-100 text-blue-700' },
      midyear: { label: 'Mid-Year', cls: 'bg-amber-100 text-amber-700' },
      endofyear: { label: 'End of Year', cls: 'bg-green-100 text-green-700' },
    };
    return map[type] || { label: type || '—', cls: 'bg-gray-100 text-gray-600' };
  };

  const filtered = filterSchoolId.trim()
    ? reports.filter(r => String(r.school_id).includes(filterSchoolId.trim()) ||
        getStudentName(r.school_id).toLowerCase().includes(filterSchoolId.trim().toLowerCase()))
    : reports;

  const statusIcon = (s) => {
    if (s === 'done') return <span className="text-green-500 font-bold">✓</span>;
    if (s === 'error') return <span className="text-red-500 font-bold">✗</span>;
    if (s === 'uploading') return <span className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin inline-block" />;
    return <span className="text-gray-300">–</span>;
  };

  return (
    <div className="space-y-6">
      {/* Upload card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        {/* Tab switcher */}
        <div className="flex gap-1 p-1 bg-gray-100 rounded-xl w-fit mb-5">
          {[['single', '📄 Single'], ['bulk', '📦 Bulk Upload']].map(([val, lbl]) => (
            <button key={val} onClick={() => { setUploadTab(val); setMsg(null); }}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${uploadTab === val ? 'bg-white shadow text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}>
              {lbl}
            </button>
          ))}
        </div>

        {/* Shared year + type */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Academic Year *</label>
            <select value={uploadTab === 'single' ? sYear : bulkYear}
              onChange={e => uploadTab === 'single' ? setSYear(e.target.value) : setBulkYear(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 bg-white">
              <option value="">Select year…</option>
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Report Type *</label>
            <select value={uploadTab === 'single' ? sType : bulkType}
              onChange={e => uploadTab === 'single' ? setSType(e.target.value) : setBulkType(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 bg-white">
              <option value="">Select type…</option>
              {reportTypeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>

        {/* ── SINGLE UPLOAD ── */}
        {uploadTab === 'single' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">PDF File *</label>
                <input type="file" accept=".pdf" onChange={handleSingleFileChange}
                  className="w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-indigo-50 file:text-indigo-700 file:font-medium hover:file:bg-indigo-100 cursor-pointer" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">School ID (auto-detected)</label>
                <input value={sSchoolId} onChange={e => setSSchoolId(e.target.value)} placeholder="e.g. 12345"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Label / Title</label>
                <input value={sLabel} onChange={e => setSLabel(e.target.value)} placeholder="e.g. Term 1 Report"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
              </div>
            </div>
            {msg && <div className={`mb-3 px-4 py-2 rounded-lg text-sm font-medium ${msg.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>{msg.text}</div>}
            <button onClick={handleSingleUpload} disabled={sUploading || !sFile}
              className="px-5 py-2.5 bg-indigo-600 text-white rounded-xl font-medium text-sm hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              {sUploading ? 'Uploading…' : 'Upload Report'}
            </button>
          </>
        )}

        {/* ── BULK UPLOAD ── */}
        {uploadTab === 'bulk' && (
          <>
            <p className="text-sm text-gray-500 mb-3">
              Select multiple PDFs. Each file should be named starting with the student's School ID
              (e.g. <code className="bg-gray-100 px-1 rounded">12345.pdf</code> or <code className="bg-gray-100 px-1 rounded">12345_Report.pdf</code>).
              Year and Report Type apply to <strong>all</strong> selected files.
            </p>
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-600 mb-1">PDF Files * (select multiple)</label>
              <input type="file" accept=".pdf" multiple onChange={handleBulkFilesChange}
                className="w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-indigo-50 file:text-indigo-700 file:font-medium hover:file:bg-indigo-100 cursor-pointer" />
            </div>

            {/* File preview table */}
            {bulkFiles.length > 0 && (
              <div className="border border-gray-200 rounded-xl overflow-hidden mb-4">
                <div className="bg-gray-50 px-4 py-2 flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-600 uppercase">{bulkFiles.length} file{bulkFiles.length !== 1 ? 's' : ''} selected</span>
                  {bulkUploading && <span className="text-xs text-indigo-600 font-medium">{bulkDone}/{bulkFiles.length} uploaded…</span>}
                </div>
                <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
                  {bulkFiles.map((b, i) => (
                    <div key={i} className="flex items-center gap-3 px-4 py-2">
                      <div className="w-5 flex-shrink-0 flex justify-center">{statusIcon(b.status)}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-700 truncate font-medium">{b.file.name}</p>
                        {b.errMsg && <p className="text-xs text-red-500">{b.errMsg}</p>}
                      </div>
                      <input
                        value={b.schoolId}
                        onChange={e => updateBulkFile(i, { schoolId: e.target.value })}
                        placeholder="School ID"
                        disabled={bulkUploading}
                        className="w-24 border border-gray-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-300 disabled:opacity-50"
                      />
                      <input
                        value={b.label}
                        onChange={e => updateBulkFile(i, { label: e.target.value })}
                        placeholder="Label"
                        disabled={bulkUploading}
                        className="w-36 border border-gray-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-300 disabled:opacity-50"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {msg && <div className={`mb-3 px-4 py-2 rounded-lg text-sm font-medium ${msg.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>{msg.text}</div>}

            <div className="flex items-center gap-3">
              <button onClick={handleBulkUpload} disabled={bulkUploading || bulkFiles.length === 0}
                className="px-5 py-2.5 bg-indigo-600 text-white rounded-xl font-medium text-sm hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                {bulkUploading ? `Uploading ${bulkDone}/${bulkFiles.length}…` : `Upload ${bulkFiles.length || ''} Reports`}
              </button>
              {bulkFiles.length > 0 && !bulkUploading && (
                <button onClick={() => { setBulkFiles([]); setBulkDone(0); setMsg(null); }}
                  className="px-4 py-2.5 text-gray-500 hover:text-gray-700 text-sm transition-colors">
                  Clear
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* Reports list */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="font-bold text-gray-800 text-lg">📂 Uploaded Reports ({reports.length})</h3>
          <input value={filterSchoolId} onChange={e => setFilterSchoolId(e.target.value)}
            placeholder="Filter by student name or ID…"
            className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm w-60 focus:outline-none focus:ring-2 focus:ring-indigo-300" />
        </div>
        {loading && <div className="flex justify-center py-8"><div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" /></div>}
        {!loading && filtered.length === 0 && (
          <div className="text-center py-10 text-gray-400">
            <p className="text-3xl mb-2">📭</p>
            <p className="text-sm">No reports uploaded yet</p>
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold text-gray-500 uppercase border-b">
                <th className="pb-2 pr-4">Student</th>
                <th className="pb-2 pr-4">School ID</th>
                <th className="pb-2 pr-4">Label</th>
                <th className="pb-2 pr-4">Year</th>
                <th className="pb-2 pr-4">Type</th>
                <th className="pb-2 pr-4">Uploaded</th>
                <th className="pb-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(r => {
                const badge = getTypeBadge(r.report_type);
                return (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="py-2 pr-4 font-medium text-gray-800">{getStudentName(r.school_id)}</td>
                    <td className="py-2 pr-4 text-gray-500 font-mono text-xs">{r.school_id}</td>
                    <td className="py-2 pr-4 text-gray-600 truncate max-w-[160px]">{r.label || r.file_name}</td>
                    <td className="py-2 pr-4 text-gray-500 whitespace-nowrap">{r.year || '—'}</td>
                    <td className="py-2 pr-4">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.cls}`}>{badge.label}</span>
                    </td>
                    <td className="py-2 pr-4 text-gray-400 whitespace-nowrap">{formatDate(r.uploaded_at)}</td>
                    <td className="py-2 flex gap-2">
                      <a href={buildApiUrl(`/api/student-reports/download/${r.id}`)} target="_blank" rel="noreferrer"
                        className="px-3 py-1 bg-indigo-50 text-indigo-700 rounded-lg text-xs font-medium hover:bg-indigo-100 transition-colors">
                        Download
                      </a>
                      <button onClick={() => handleDelete(r.id)}
                        className="px-3 py-1 bg-red-50 text-red-600 rounded-lg text-xs font-medium hover:bg-red-100 transition-colors">
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────

export default function SchoolManager({ students: propStudents = [], defaultTab = "ClassManager", hideNavigation = false, onBack }) {
  const [navTab, setNavTab] = useState(defaultTab);
  const [settingsTab, setSettingsTab] = useState("Classes");
  const [activeReportTab, setActiveReportTab] = useState("pupils-overview");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // 🔔 Notification settings state
  const [notificationSettings, setNotificationSettings] = useState([]);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
  const [notifSaveStatus, setNotifSaveStatus] = useState("");
  const [students, setStudents] = useState(propStudents);
  const [selectedForm, setSelectedForm] = useState("");
  const [teachers, setTeachers] = useState([]);
  
  // Applicants state
  const [applicantsData, setApplicantsData] = useState([]);
  const [isLoadingApplicants, setIsLoadingApplicants] = useState(false);
  const [applicantsSchool, setApplicantsSchool] = useState("");
  const [applicantsSchoolYear, setApplicantsSchoolYear] = useState("");
  const [applicantsYear, setApplicantsYear] = useState('2025');
  const [applicantsMonth, setApplicantsMonth] = useState("");
  const [applicantsAdmissionStatus, setApplicantsAdmissionStatus] = useState("");
  const [applicantsStartingYear, setApplicantsStartingYear] = useState("");
  
  // Leavers state
  const [leaversData, setLeaversData] = useState([]);
  const [isLoadingLeavers, setIsLoadingLeavers] = useState(false);
  const [leaversSchool, setLeaversSchool] = useState("");
  const [leaversYear, setLeaversYear] = useState("");
  const [leaversMonth, setLeaversMonth] = useState("");
  const [formAssignments, setFormAssignments] = useState({});

  // Load applicants and leavers from database on mount
  useEffect(() => {
    const loadSavedData = async () => {
      try {
        // Load applicants from database with longer timeout
        const applicantsRes = await axios.get(buildApiUrl('/api/school/applicants'), {
          timeout: 60000 // 60 seconds timeout
        });
        if (applicantsRes.data.success && applicantsRes.data.applicants.length > 0) {
          setApplicantsData(applicantsRes.data.applicants);
          console.log(`📦 Loaded ${applicantsRes.data.applicants.length} applicants from database`);
        }
        
        // Load leavers from database with longer timeout
        const leaversRes = await axios.get(buildApiUrl('/api/school/leavers'), {
          timeout: 60000 // 60 seconds timeout
        });
        if (leaversRes.data.success && leaversRes.data.leavers.length > 0) {
          setLeaversData(leaversRes.data.leavers);
          console.log(`📦 Loaded ${leaversRes.data.leavers.length} leavers from database`);
        }
      } catch (error) {
        console.error('❌ Error loading from database:', error);
        if (error.code === 'ECONNABORTED') {
          console.error('⏱️ Request timed out - data might be too large');
        }
      }
    };
    
    loadSavedData();
  }, []);

  // 🔔 Fetch notification settings when Settings > Notifications tab is selected
  useEffect(() => {
    if (navTab === "Settings" && settingsTab === "Notifications") {
      const fetchNotifSettings = async () => {
        setIsLoadingNotifications(true);
        try {
          const res = await axios.get(buildApiUrl('/api/notification-settings'));
          if (res.data.success) {
            setNotificationSettings(res.data.settings);
          }
        } catch (err) {
          console.error('❌ Error loading notification settings:', err);
        } finally {
          setIsLoadingNotifications(false);
        }
      };
      fetchNotifSettings();
    }
  }, [navTab, settingsTab]);

  // 🔔 Toggle a notification setting
  const handleToggleNotification = async (moduleId, field, newValue) => {
    try {
      setNotifSaveStatus("Saving...");
      const res = await axios.put(buildApiUrl(`/api/notification-settings/${moduleId}`), {
        [field]: newValue
      });
      if (res.data.success) {
        setNotificationSettings(prev =>
          prev.map(s => s.module_id === moduleId ? { ...s, [field]: newValue } : s)
        );
        setNotifSaveStatus("✅ Saved");
        setTimeout(() => setNotifSaveStatus(""), 2000);
      }
    } catch (err) {
      console.error('❌ Error updating notification setting:', err);
      setNotifSaveStatus("❌ Error saving");
      setTimeout(() => setNotifSaveStatus(""), 3000);
    }
  };

  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [selectedOwners, setSelectedOwners] = useState([]);
  const [assignmentMode, setAssignmentMode] = useState('teachers'); // 'teachers' or 'owners'
  const [teacherSearchTerm, setTeacherSearchTerm] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  
  // Reports state
  const [registrationData, setRegistrationData] = useState({});
  const [isLoadingRegistration, setIsLoadingRegistration] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  
  // Clubs Attendance state
  const [clubsData, setClubsData] = useState([]);
  const [clubsAttendanceHistory, setClubsAttendanceHistory] = useState([]);
  const [isLoadingClubsAttendance, setIsLoadingClubsAttendance] = useState(false);
  const [activeClubsAttendanceTab, setActiveClubsAttendanceTab] = useState("all-clubs");
  const [selectedClubForAttendance, setSelectedClubForAttendance] = useState(null);
  
  // 🆕 Get user's school assignment for filtering
  const currentUser = authService.getCurrentUser();
  const userSchoolAssignment = React.useMemo(() => {
    if (!currentUser?.email) return null;
    
    // Check school assignments from localStorage
    const schoolAssignments = JSON.parse(localStorage.getItem('schoolAssignments') || '{}');
    let assignment = schoolAssignments[currentUser.email];
    
    // Fallback: Detect school from domain if no assignment in localStorage
    if (!assignment) {
      const hostname = window.location.hostname;
      if (hostname.includes('sherborneboys')) {
        assignment = { school: 'Boys School' };
        console.log('🏫 Auto-detected school from domain: Boys School');
      } else if (hostname.includes('sherbornebh')) {
        assignment = { school: 'Girls School' };
        console.log('🏫 Auto-detected school from domain: Girls School (BH)');
      } else {
        assignment = { school: 'All Schools' };
        console.log('🏫 Defaulting to All Schools');
      }
    }
    
    return assignment;
  }, [currentUser?.email]);

  // Fetch applicants function (used by both ApplicantsReport and CapacityReport)
  const fetchApplicants = async (forceRefresh = false) => {
    setIsLoadingApplicants(true);
    try {
      const refreshParam = forceRefresh ? '?refresh=true' : '';
      console.log(`🔄 Fetching applicants from API${forceRefresh ? ' (force refresh)' : ' (using cache if available)'}...`);
      const response = await axios.get(buildApiUrl(`/api/admissions/applicants${refreshParam}`));
      
      console.log('📥 API Response:', response.data);
      
      if (response.data.success) {
        const allApplicants = response.data.applicants || [];
        console.log(`📊 Total applicants from API: ${allApplicants.length}`);
        console.log('📋 Sample applicant data:', allApplicants?.[0]);
        
        // Filter to last 5 years only when called from Settings tab
        const currentYear = new Date().getFullYear();
        const fiveYearsAgo = currentYear - 5;
        const filteredApplicants = forceRefresh 
          ? allApplicants.filter(applicant => {
              const dateStr = applicant.application_date || applicant.start_date || applicant.created_date || applicant.date;
              if (!dateStr) {
                console.log('⚠️ No date field found for applicant:', applicant);
                return true; // Include if no date found
              }
              const appDate = new Date(dateStr);
              const year = appDate.getFullYear();
              const isValid = !isNaN(year) && year >= fiveYearsAgo;
              if (!isValid) {
                console.log(`🚫 Filtered out: ${applicant.full_name || applicant.name} - Year: ${year}`);
              }
              return isValid || isNaN(year); // Include if date is invalid
            })
          : allApplicants;
        
        console.log(`✅ After filtering: ${filteredApplicants.length} applicants (from ${allApplicants.length} total)`);
        setApplicantsData(filteredApplicants);
        
        // Save to database
        try {
          console.log(`🔄 Saving ${filteredApplicants.length} applicants to database...`);
          const saveRes = await axios.post(buildApiUrl('/api/school/save-applicants'), {
            applicants: filteredApplicants
          });
          console.log('📥 Save response:', saveRes.data);
          if (saveRes.data.success) {
            console.log(`💾 Saved ${filteredApplicants.length} applicants to database (ID: ${saveRes.data.id})`);
          } else {
            console.error('❌ Save failed:', saveRes.data.error);
          }
        } catch (error) {
          console.error('❌ Error saving applicants to database:', error);
          console.error('❌ Error details:', error.response?.data);
        }
        
        // Update global cache
        if (globalDataCache) {
          globalDataCache.applicants = filteredApplicants;
        }
        
        const cacheStatus = response.data.cached ? ` (cached from ${response.data.cachedAt})` : ' (fresh from API)';
        console.log(`✅ Loaded ${filteredApplicants.length} applicants (filtered from ${allApplicants.length})${cacheStatus}`);
        console.log('📊 Sample filtered applicant:', filteredApplicants?.[0]);
        console.log('📋 Available fields:', filteredApplicants?.[0] ? Object.keys(filteredApplicants[0]) : 'No data');
      } else {
        console.error('❌ API returned error:', response.data.error);
        setApplicantsData([]);
      }
    } catch (error) {
      console.error('❌ Error fetching applicants:', error);
      console.error('Error details:', error.response?.data);
      setApplicantsData([]);
    } finally {
      setIsLoadingApplicants(false);
    }
  };

  // Fetch leavers function
  const fetchLeavers = async (forceRefresh = false) => {
    setIsLoadingLeavers(true);
    try {
      const refreshParam = forceRefresh ? '?refresh=true' : '';
      console.log(`🔄 Fetching leavers from API${forceRefresh ? ' (force refresh)' : ' (using cache if available)'}...`);
      const response = await axios.get(buildApiUrl(`/api/alumni${refreshParam}`));
      
      console.log('📥 Leavers API Response:', response.data);
      
      if (response.data.success) {
        const leaversArray = response.data.leavers || [];
        
        // Save to database
        try {
          console.log(`🔄 Saving ${leaversArray.length} leavers to database...`);
          const saveRes = await axios.post(buildApiUrl('/api/school/save-leavers'), {
            leavers: leaversArray
          });
          console.log('📥 Save response:', saveRes.data);
          if (saveRes.data.success) {
            console.log(`💾 Saved ${leaversArray.length} leavers to database (ID: ${saveRes.data.id})`);
          } else {
            console.error('❌ Save failed:', saveRes.data.error);
          }
        } catch (error) {
          console.error('❌ Error saving leavers to database:', error);
          console.error('❌ Error details:', error.response?.data);
        }
        
        // Update global cache
        if (globalDataCache) {
          globalDataCache.leavers = leaversArray;
        }
        setLeaversData(leaversArray);
        console.log(`✅ Loaded ${leaversArray.length} leavers`);
        console.log(`📊 Main School count: ${response.data.mainSchoolCount || 0}`);
        console.log(`📊 Girls School count: ${response.data.girlsSchoolCount || 0}`);
        
        // Debug: Check sample record structure
        if (leaversArray.length > 0) {
          console.log('📋 Sample leaver record:', leaversArray[0]);
          console.log('📋 Available fields:', Object.keys(leaversArray[0]));
          console.log('📋 Full first record:', JSON.stringify(leaversArray[0], null, 2));
          
          const schoolValues = leaversArray.slice(0, 5).map(l => l.school);
          const yearGroupValues = leaversArray.slice(0, 5).map(l => l.enrolmentSchoolYearGroup);
          
          console.log('📋 School field values:', JSON.stringify(schoolValues));
          console.log('📋 Year group field values:', JSON.stringify(yearGroupValues));
          console.log('📋 Leaving year groups:', JSON.stringify(leaversArray.slice(0, 5).map(l => l.leavingYearGroup)));
          console.log('📋 Leaving dates:', JSON.stringify(leaversArray.slice(0, 5).map(l => l.leavingDate)));
          console.log('📋 Last updated values:', JSON.stringify(leaversArray.slice(0, 5).map(l => l.lastUpdated)));
        }
      } else {
        console.error('❌ API returned error:', response.data.error);
        setLeaversData([]);
      }
    } catch (error) {
      console.error('❌ Error fetching leavers:', error);
      console.error('Error details:', error.response?.data);
      setLeaversData([]);
    } finally {
      setIsLoadingLeavers(false);
    }
  };

  useEffect(() => {
    // Check if we already have fresh data in cache
    const now = Date.now();
    
    if (globalDataCache && (now - globalCacheTimestamp) < CACHE_DURATION) {
      console.log('📦 Using cached data');
      setStudents(globalDataCache.students);
      // Deduplicate teachers by email
      const uniqueTeachers = Array.from(new Map(globalDataCache.teachers.map(t => [t.email, t])).values());
      setTeachers(uniqueTeachers);
      setFormAssignments(globalDataCache.assignments);
      setApplicantsData(globalDataCache.applicants || []);
      setLeaversData(globalDataCache.leavers || []);
      setDataLoaded(true);
      return;
    }

    // Prevent multiple simultaneous calls
    if (isGloballyFetching || isLoadingData || dataLoaded) {
      console.log('🚫 Skipping fetch - already in progress or loaded');
      return;
    }

    const fetchAllData = async () => {
      if (isGloballyFetching) {
        console.log('🚫 Another fetch already in progress');
        return;
      }

      isGloballyFetching = true;
      setIsLoadingData(true);
      console.log('🚀 Starting data fetch...');
      
      try {
        let studentsData = propStudents;
        
        // Only fetch students if not provided as props
        if (propStudents.length === 0) {
          console.log('📊 Fetching students...');
          // Determine which school to fetch based on user's school assignment
          let schoolParam = "";
          if (userSchoolAssignment?.school === 'Boys School') {
            schoolParam = "boys";
          } else if (userSchoolAssignment?.school === 'Girls School') {
            schoolParam = "girls";
          }
          
          const studentsUrl = schoolParam
            ? buildApiUrl(`/synced-students?school=${schoolParam}`)
            : buildApiUrl('/synced-students');
          
          console.log(`📊 Fetching ${schoolParam || 'all'} school students from ${studentsUrl}...`);
          const studentsRes = await axios.get(studentsUrl);
          studentsData = studentsRes.data.students;
          console.log(`✅ Loaded ${studentsData.length} students`);
        }

        console.log('👥 Fetching teachers...');
        const teachersRes = await axios.get(buildApiUrl('/staff.json'));
        
        console.log('📋 Fetching assignments...');
        // Add cache-busting query parameter to force fresh data
        const assignmentsRes = await axios.get(buildApiUrl(`/teacher_assignments.json?t=${Date.now()}`));
        
        // Cache the data globally
        globalDataCache = {
          students: studentsData,
          teachers: teachersRes.data,
          assignments: assignmentsRes.data,
          applicants: globalDataCache?.applicants || [],
          leavers: globalDataCache?.leavers || []
        };
        globalCacheTimestamp = Date.now();
        
        // Set local state
        setStudents(studentsData);
        // Deduplicate teachers by email
        const uniqueTeachers = Array.from(new Map(teachersRes.data.map(t => [t.email, t])).values());
        setTeachers(uniqueTeachers);
        setFormAssignments(assignmentsRes.data);
        setDataLoaded(true);
        
        console.log('✅ Data fetch completed successfully');
      } catch (err) {
        console.error("❌ Failed to fetch school data:", err);
        
        // Set default empty data
        setStudents(propStudents.length > 0 ? propStudents : []);
        setTeachers([]);
        setFormAssignments({});
        setDataLoaded(true); // Mark as loaded to prevent retries
      } finally {
        isGloballyFetching = false;
        setIsLoadingData(false);
      }
    };

    fetchAllData();
  }, []); // Empty dependency array to run only once

  const handleSyncNow = async () => {
    try {
      // Determine which school to sync based on user's school assignment
      let schoolParam = "";
      if (userSchoolAssignment?.school === 'Boys School') {
        schoolParam = "boys";
      } else if (userSchoolAssignment?.school === 'Girls School') {
        schoolParam = "girls";
      }
      
      const syncUrl = schoolParam 
        ? buildApiUrl(`/sync-isams?school=${schoolParam}`)
        : buildApiUrl('/sync-isams');
      
      console.log(`📤 Syncing from ${schoolParam || 'all schools'}...`);
      await axios.post(syncUrl); // triggers backend sync!
      
      // Re-fetch the fresh students data with same school filter
      const fetchUrl = schoolParam
        ? buildApiUrl(`/synced-students?school=${schoolParam}`)
        : buildApiUrl('/synced-students');
      
      const studentsRes = await axios.get(fetchUrl);
      setStudents(studentsRes.data.students);
      alert(`Sync successful! (${studentsRes.data.students.length} students)`);
    } catch (err) {
      alert("Sync failed. Please check backend.");
      console.error("Sync failed:", err);
    }
  };

  // 🆕 Filter students by school assignment BEFORE deduping
  const schoolFilteredStudents = React.useMemo(() => {
    if (!userSchoolAssignment || !userSchoolAssignment.school || userSchoolAssignment.school === 'All Schools') {
      console.log('📊 SchoolManager: Showing all students (no restriction)');
      return students;
    }
    
    // Map school names to division_name prefixes or exact values
    const schoolToDivisionMap = {
      'Boys School': ['BS ', 'Boys'],  // Boys school division
      'Girls School': ['GS '],
      'BaniHajer School': ['BH ', 'Bani Hajer'],
      'MOQ School': ['MOQ ', 'Prep Mall of Qatar']
    };
    
    const allowedPrefixes = schoolToDivisionMap[userSchoolAssignment.school] || [];
    
    if (allowedPrefixes.length === 0) {
      console.log('📊 SchoolManager: No matching division prefixes, showing all');
      return students;
    }
    
    const filtered = students.filter(student => {
      if (!student.division_name) return false;
      // Check if division_name starts with prefix OR matches exactly
      return allowedPrefixes.some(prefix => 
        student.division_name.startsWith(prefix) || student.division_name === prefix
      );
    });
    
    console.log(`📊 SchoolManager: Filtered ${students.length} students to ${filtered.length} for ${userSchoolAssignment.school}`);
    return filtered;
  }, [students, userSchoolAssignment]);

  const dedupedPupils = React.useMemo(() => getDedupedPupils(schoolFilteredStudents), [schoolFilteredStudents]);
  const forms = React.useMemo(() => getFormsList(schoolFilteredStudents), [schoolFilteredStudents]);

  const dynamicYearFormMap = React.useMemo(() => {
    const map = {};
    schoolFilteredStudents.forEach(student => {
        // Use nc_year (from backend) or year (fallback) for year group
        const yearGroup = student.nc_year || student.year;
        if (!yearGroup || !student.form) return;
        if (!map[yearGroup]) {
            map[yearGroup] = new Set();
        }
        map[yearGroup].add(student.form);
    });
    Object.keys(map).forEach(year => {
        map[year] = Array.from(map[year]).sort();
    });
    return map;
  }, [schoolFilteredStudents]);

  // Use ONLY dynamic forms from synced students (not static/hardcoded ones)
  const mergedYearFormMap = React.useMemo(() => {
    return dynamicYearFormMap;
  }, [dynamicYearFormMap]);

  const handleFormClick = (form) => {
    setSelectedForm(form);
    setShowDropdown(false);
    const formData = formAssignments[form] || {};
    
    // Handle new format with owners and teachers
    if (typeof formData === 'object' && !Array.isArray(formData)) {
      setSelectedOwners(formData.owners || []);
      setSelectedTeachers(formData.teachers || []);
    } else {
      // Legacy format - treat as teachers
      setSelectedOwners([]);
      setSelectedTeachers(Array.isArray(formData) ? formData : []);
    }
  };

  const handleAssignClick = () => {
    setShowDropdown(true);
  };

  const updateAssignment = async (form, owners, teachersArray) => {
    try {
      await axios.post(buildApiUrl('/class-assignments'), { 
        form, 
        owners: owners || [], 
        teachers: teachersArray || [] 
      });
      setFormAssignments(prev => ({ 
        ...prev, 
        [form]: {
          owners: owners || [],
          teachers: teachersArray || []
        }
      }));
    } catch (err) {
      console.error("Failed to update assignment:", err);
    }
  };

  const handleTeacherSelect = async (event) => {
    const options = Array.from(event.target.selectedOptions, option => option.value);
    if (assignmentMode === 'owners') {
      setSelectedOwners(options);
    } else {
      setSelectedTeachers(options);
    }
  };

  const handleRemoveTeacher = async (emailToRemove) => {
    if (assignmentMode === 'owners') {
      const updatedOwners = selectedOwners.filter(email => email !== emailToRemove);
      setSelectedOwners(updatedOwners);
      await updateAssignment(selectedForm, updatedOwners, selectedTeachers);
    } else {
      const updatedTeachers = selectedTeachers.filter(email => email !== emailToRemove);
      setSelectedTeachers(updatedTeachers);
      await updateAssignment(selectedForm, selectedOwners, updatedTeachers);
    }
  };

  const handleDoneAssigning = async () => {
    await updateAssignment(selectedForm, selectedOwners, selectedTeachers);
    setShowDropdown(false);
  };

  const filteredStudents = dedupedPupils.filter(s => {
    const matchesForm = s.form?.trim().toLowerCase() === selectedForm.trim().toLowerCase();
    const matchesSearch = !searchTerm ||
      s.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.school_id?.includes(searchTerm);
    return matchesForm && matchesSearch;
  });

  const getYearLabel = (year) => {
    return getYearLabelUtil(year);
  };

  const getFormStats = (form) => {
    const formStudents = dedupedPupils.filter(s => s.form === form);
    const maleCount = formStudents.filter(s => s.gender === 'M').length;
    const femaleCount = formStudents.filter(s => s.gender === 'F').length;
    return { total: formStudents.length, male: maleCount, female: femaleCount };
  };

  // Get total pupils statistics
  const getTotalPupilsStats = () => {
    const totalCount = dedupedPupils.length;
    const maleCount = dedupedPupils.filter(s => s.gender === 'M').length;
    const femaleCount = dedupedPupils.filter(s => s.gender === 'F').length;
    return { total: totalCount, male: maleCount, female: femaleCount };
  };

  // Load clubs attendance data
  const loadClubsAttendanceData = async () => {
    setIsLoadingClubsAttendance(true);
    try {
      // Fetch all clubs
      const clubsResponse = await axios.get(buildApiUrl('/api/clubs'));
      const clubs = clubsResponse.data.clubs || [];
      setClubsData(clubs);

      // Fetch attendance data for all clubs
      const attendanceByClub = {};
      for (const club of clubs) {
        try {
          const attendanceResponse = await axios.get(
            buildApiUrl(`/api/clubs/${club.id}/attendance`)
          );
          if (attendanceResponse.data.success) {
            attendanceByClub[club.id] = attendanceResponse.data.attendance_records || [];
          }
        } catch (error) {
          console.error(`Failed to load attendance for club ${club.id}:`, error);
          attendanceByClub[club.id] = [];
        }
      }
      
      setClubsAttendanceHistory(attendanceByClub);
      console.log('✅ Loaded clubs attendance data');
    } catch (error) {
      console.error('❌ Error loading clubs attendance:', error);
      alert('Failed to load clubs attendance data');
    } finally {
      setIsLoadingClubsAttendance(false);
    }
  };

  // Load clubs attendance when tab is activated
  useEffect(() => {
    if (navTab === "ClubsAttendance" && clubsData.length === 0) {
      loadClubsAttendanceData();
    }
  }, [navTab]);

  const filteredYears = selectedYear ? [selectedYear] : Object.keys(mergedYearFormMap).sort((a, b) => Number(a) - Number(b));

  return (
    <div className="flex min-h-screen flex-col lg:flex-row">
      {/* Loading Overlay */}
      {isLoadingData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="text-gray-700">Loading school data...</span>
            </div>
          </div>
        </div>
      )}
      
      {/* Mobile Menu Button - Only show if navigation is not hidden */}
      {!hideNavigation && (
        <div className="lg:hidden sticky top-0 z-50 bg-slate-800 text-white px-4 py-3 flex items-center justify-between">
          <h1 className="text-lg font-bold">Admin Panel</h1>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
            title="Toggle Menu"
          >
            {mobileMenuOpen ? (
              <span className="text-2xl">✕</span>
            ) : (
              <span className="text-2xl">☰</span>
            )}
          </button>
        </div>
      )}

      {/* Left Nav - Sidebar - Only show if navigation is not hidden */}
      {!hideNavigation && (
        <div className={`${
          mobileMenuOpen ? 'block' : 'hidden'
        } lg:block w-56 bg-slate-800 text-white flex flex-col py-6 px-4 lg:sticky lg:top-0 lg:h-screen overflow-y-auto`}>
        {onBack && (
          <button
            onClick={onBack}
            className="mb-3 flex items-center gap-2 text-slate-300 hover:text-white px-3 py-2 rounded-xl hover:bg-slate-700 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Dashboard
          </button>
        )}
        <h1 className="text-xl font-bold text-white px-3 mb-4 hidden lg:block">Admin Panel</h1>
        <button
          className={`mb-2 text-left px-3 py-2 rounded-xl ${navTab === "Home" ? "bg-slate-700 font-bold" : "hover:bg-slate-700"}`}
          onClick={() => {
            setNavTab("Home");
            setMobileMenuOpen(false);
          }}
        >
          🏠 Home
        </button>
        <button
          className={`mb-2 text-left px-3 py-2 rounded-xl ${navTab === "ClassManager" ? "bg-slate-700 font-bold" : "hover:bg-slate-700"}`}
          onClick={() => {
            setNavTab("ClassManager");
            setMobileMenuOpen(false);
          }}
        >
          👥 Class Manager
        </button>
        <button
          className={`mb-2 text-left px-3 py-2 rounded-xl ${navTab === "Timetable" ? "bg-slate-700 font-bold" : "hover:bg-slate-700"}`}
          onClick={() => {
            setNavTab("Timetable");
            setMobileMenuOpen(false);
          }}
        >
          📅 Timetable
        </button>
        <button
          className={`mb-2 text-left px-3 py-2 rounded-xl ${navTab === "ParentsEvening" ? "bg-slate-700 font-bold" : "hover:bg-slate-700"}`}
          onClick={() => {
            setNavTab("ParentsEvening");
            setMobileMenuOpen(false);
          }}
        >
          🎓 Parents Evening
        </button>
        <button
          className={`mb-2 text-left px-3 py-2 rounded-xl ${navTab === "EmergencyAlerts" ? "bg-slate-700 font-bold" : "hover:bg-slate-700"}`}
          onClick={() => {
            setNavTab("EmergencyAlerts");
            setMobileMenuOpen(false);
          }}
        >
          🚨 Emergency Alerts
        </button>
        <button
          className={`mb-2 text-left px-3 py-2 rounded-xl ${navTab === "ClubBooking" ? "bg-slate-700 font-bold" : "hover:bg-slate-700"}`}
          onClick={() => {
            setNavTab("ClubBooking");
            setMobileMenuOpen(false);
          }}
        >
          🏃‍♂️ Club Booking
        </button>
        <button
          className={`mb-2 text-left px-3 py-2 rounded-xl ${navTab === "QuickLinks" ? "bg-slate-700 font-bold" : "hover:bg-slate-700"}`}
          onClick={() => {
            setNavTab("QuickLinks");
            setMobileMenuOpen(false);
          }}
        >
          🔗 Quick Links
        </button>
        <button
          className={`mb-2 text-left px-3 py-2 rounded-xl ${navTab === "ConsentForms" ? "bg-slate-700 font-bold" : "hover:bg-slate-700"}`}
          onClick={() => {
            setNavTab("ConsentForms");
            setMobileMenuOpen(false);
          }}
        >
          📋 Consent Forms
        </button>
        <button
          className={`mb-2 text-left px-3 py-2 rounded-xl ${navTab === "SchoolPolicies" ? "bg-slate-700 font-bold" : "hover:bg-slate-700"}`}
          onClick={() => {
            setNavTab("SchoolPolicies");
            setMobileMenuOpen(false);
          }}
        >
          📚 School Policies
        </button>
        <button
          className={`mb-2 text-left px-3 py-2 rounded-xl ${navTab === "ParentsManager" ? "bg-slate-700 font-bold" : "hover:bg-slate-700"}`}
          onClick={() => {
            setNavTab("ParentsManager");
            setMobileMenuOpen(false);
          }}
        >
          👨‍👩‍👧‍👦 Parents Manager
        </button>
        <button
          className={`mb-2 text-left px-3 py-2 rounded-xl ${navTab === "Reports" ? "bg-slate-700 font-bold" : "hover:bg-slate-700"}`}
          onClick={() => {
            setNavTab("Reports");
            setMobileMenuOpen(false);
          }}
        >
          📊 Reports
        </button>
        <button
          className={`mb-2 text-left px-3 py-2 rounded-xl ${navTab === "UserManagement" ? "bg-slate-700 font-bold" : "hover:bg-slate-700"}`}
          onClick={() => {
            setNavTab("UserManagement");
            setMobileMenuOpen(false);
          }}
        >
          👥 User Management
        </button>
        <button
          className={`mb-2 text-left px-3 py-2 rounded-xl ${navTab === "Settings" ? "bg-slate-700 font-bold" : "hover:bg-slate-700"}`}
          onClick={() => {
            setNavTab("Settings");
            setMobileMenuOpen(false);
          }}
        >
          ⚙️ Settings
        </button>
        </div>
      )}
      {/* Main Content */}
      <div className="flex-1 bg-slate-50 px-4 lg:px-8 py-6 overflow-x-auto">
        {navTab === "Home" && (
          <div>
            <h1 className="text-2xl font-bold mb-2">Welcome to School Manager</h1>
            <p className="text-lg text-slate-600">Select a tab from the left to get started.</p>
          </div>
        )}

        {navTab === "ClassManager" && (
          <div className="bg-gray-50">
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold">🏫</span>
                </div>
                <h1 className="text-3xl font-bold text-gray-900">Class Manager</h1>
              </div>
              <p className="text-gray-600">Manage classes, teachers, and student assignments</p>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Year</label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                  >
                    <option value="">All Years</option>
                    {Object.keys(mergedYearFormMap).sort((a, b) => Number(a) - Number(b)).map(year => (
                      <option key={year} value={year}>{getYearLabel(year)}</option>
                    ))}
                  </select>
                </div>
                {selectedForm && (
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Search Students</label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Search by name or ID..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Forms Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
              {filteredYears.map(year => (
                <div key={year} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <span className="text-blue-600 font-bold">{getYearLabelUtil(year).replace('Pre-School','PS').replace('Reception','R').replace('Year ','')}</span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">{getYearLabel(year)}</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {mergedYearFormMap[year].map(form => {
                      const stats = getFormStats(form);
                      // Get assignment data for this form
                      const formData = formAssignments[form] || {};
                      const owners = typeof formData === 'object' && !Array.isArray(formData) 
                        ? formData.owners || []
                        : [];
                      const teacherEmails = typeof formData === 'object' && !Array.isArray(formData)
                        ? formData.teachers || []
                        : Array.isArray(formData) ? formData : [];
                      
                      return (
                        <div key={form} className="relative">
                          <button
                            className={`p-3 rounded-lg border-2 transition-all duration-200 text-left w-full ${
                              selectedForm === form 
                                ? "border-blue-600 bg-blue-50" 
                                : "border-gray-200 hover:border-blue-300 hover:bg-blue-50"
                            }`}
                            onClick={() => handleFormClick(form)}
                          >
                            <div className="font-semibold text-gray-900 flex items-center gap-2">
                              {form}
                              {/* Show owners and teachers */}
                              <div className="flex flex-wrap gap-1">
                                {owners.map(email => (
                                  <span
                                    key={email}
                                    className="inline-flex items-center bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full text-xs ml-1"
                                    title="Class Owner"
                                  >
                                    👑 {teachers.find(t => t.email === email)?.name || email}
                                  </span>
                                ))}
                                {teacherEmails.filter(email => !owners.includes(email)).map(email => (
                                  <span
                                    key={email}
                                    className="inline-flex items-center bg-green-100 text-green-800 px-2 py-0.5 rounded-full text-xs ml-1"
                                    title="Class Teacher"
                                  >
                                    {teachers.find(t => t.email === email)?.name || email}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {stats.total} students
                            </div>
                            {stats.total > 0 && (
                              <div className="text-xs text-gray-400 mt-1">
                                {stats.male}M • {stats.female}F
                              </div>
                            )}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Selected Form Details */}
            {selectedForm && (
              <div className="space-y-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                        <span className="text-white font-bold text-lg">{selectedForm}</span>
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900">Form {selectedForm}</h2>
                        <p className="text-gray-600">{filteredStudents.length} students</p>
                      </div>
                    </div>
                    <button
                      className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                      onClick={handleAssignClick}
                    >
                      Assign Teacher
                    </button>
                  </div>

                  {/* Teacher Assignment */}
                  {showDropdown && (
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                      {/* Assignment Mode Toggle */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Assignment Type</label>
                        <div className="flex gap-2">
                          <button
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              assignmentMode === 'owners' 
                                ? 'bg-purple-600 text-white' 
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                            onClick={() => setAssignmentMode('owners')}
                          >
                            👑 Class Owners
                          </button>
                          <button
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              assignmentMode === 'teachers' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                            onClick={() => setAssignmentMode('teachers')}
                          >
                            👨‍🏫 Class Teachers
                          </button>
                        </div>
                        <p className="text-xs text-gray-600 mt-1">
                          {assignmentMode === 'owners' 
                            ? 'Class owners have primary responsibility and Fire Register access'
                            : 'Class teachers can assist with teaching but don\'t have Fire Register access'
                          }
                        </p>
                      </div>

                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select {assignmentMode === 'owners' ? 'Class Owner(s)' : 'Teacher(s)'}
                      </label>
                      
                      {/* Search bar for teachers */}
                      <div className="mb-3">
                        <input
                          type="text"
                          placeholder="Search teachers by name or email..."
                          className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          value={teacherSearchTerm}
                          onChange={(e) => setTeacherSearchTerm(e.target.value)}
                        />
                        {teacherSearchTerm && teacherSearchTerm.trim() !== '' && (() => {
                          const filteredCount = teachers.filter(teacher => {
                            const searchLower = teacherSearchTerm.toLowerCase().trim();
                            return teacher.name?.toLowerCase().includes(searchLower) ||
                                   teacher.email?.toLowerCase().includes(searchLower);
                          }).length;
                          return (
                            <div className="mt-1 text-xs text-blue-600 font-medium">
                              Showing {filteredCount} of {teachers.length} teachers
                            </div>
                          );
                        })()}
                      </div>
                      
                      {/* Multi-select list with checkboxes */}
                      <div className="border border-gray-300 rounded-lg max-h-60 overflow-y-auto">
                        {(() => {
                          const filteredTeachers = teachers.filter(teacher => {
                            if (!teacherSearchTerm || teacherSearchTerm.trim() === '') return true;
                            const searchLower = teacherSearchTerm.toLowerCase().trim();
                            const matches = teacher.name?.toLowerCase().includes(searchLower) ||
                                   teacher.email?.toLowerCase().includes(searchLower);
                            return matches;
                          });
                          return filteredTeachers;
                        })().map((teacher) => {
                            const isSelected = (assignmentMode === 'owners' ? selectedOwners : selectedTeachers).includes(teacher.email);
                            return (
                              <div
                                key={teacher.email}
                                className={`flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                                  isSelected ? 'bg-blue-50' : ''
                                }`}
                                onClick={() => {
                                  const currentList = assignmentMode === 'owners' ? selectedOwners : selectedTeachers;
                                  const setList = assignmentMode === 'owners' ? setSelectedOwners : setSelectedTeachers;
                                  
                                  if (isSelected) {
                                    setList(currentList.filter(email => email !== teacher.email));
                                  } else {
                                    setList([...currentList, teacher.email]);
                                  }
                                }}
                              >
                                <input
                                  type="checkbox"
                                  className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded pointer-events-none"
                                  checked={isSelected}
                                  readOnly
                                />
                                <div className="flex-1">
                                  <div className="font-medium text-gray-900">{teacher.name}</div>
                                  <div className="text-xs text-gray-500">{teacher.email}</div>
                                </div>
                              </div>
                            );
                          })}
                        {(() => {
                          const filteredCount = teachers.filter(teacher => {
                            if (!teacherSearchTerm || teacherSearchTerm.trim() === '') return true;
                            const searchLower = teacherSearchTerm.toLowerCase().trim();
                            return teacher.name?.toLowerCase().includes(searchLower) ||
                                   teacher.email?.toLowerCase().includes(searchLower);
                          }).length;
                          return filteredCount === 0 && teacherSearchTerm && teacherSearchTerm.trim() !== '' && (
                            <div className="px-4 py-8 text-center text-gray-500">
                              No teachers found matching "{teacherSearchTerm}"
                            </div>
                          );
                        })()}
                      </div>
                      
                      {/* Show selected items */}
                      {(assignmentMode === 'owners' ? selectedOwners : selectedTeachers).length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {(assignmentMode === 'owners' ? selectedOwners : selectedTeachers).map(email => (
                            <span
                              key={email}
                              className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
                                assignmentMode === 'owners'
                                  ? 'bg-purple-100 text-purple-800'
                                  : 'bg-blue-100 text-blue-800'
                              }`}
                            >
                              {assignmentMode === 'owners' && '👑 '}
                              {teachers.find(t => t.email === email)?.name || email}
                              <button
                                className={`ml-2 hover:text-red-600 ${
                                  assignmentMode === 'owners' ? 'text-purple-600' : 'text-blue-600'
                                }`}
                                onClick={() => handleRemoveTeacher(email)}
                                title="Remove"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Current Assignments Summary */}
                      <div className="mt-4 p-3 bg-white rounded-lg border">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Current Assignments for {selectedForm}:</h4>
                        <div className="space-y-2">
                          <div>
                            <span className="text-xs font-medium text-purple-700">Class Owners:</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {selectedOwners.length > 0 ? selectedOwners.map(email => (
                                <span key={email} className="bg-purple-100 text-purple-800 px-2 py-0.5 rounded text-xs">
                                  👑 {teachers.find(t => t.email === email)?.name || email}
                                </span>
                              )) : <span className="text-xs text-gray-500">None</span>}
                            </div>
                          </div>
                          <div>
                            <span className="text-xs font-medium text-green-700">Class Teachers:</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {selectedTeachers.length > 0 ? selectedTeachers.map(email => (
                                <span key={email} className="bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs">
                                  {teachers.find(t => t.email === email)?.name || email}
                                </span>
                              )) : <span className="text-xs text-gray-500">None</span>}
                            </div>
                          </div>
                        </div>
                      </div>

                      <button
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                        onClick={handleDoneAssigning}
                      >
                        Save Assignments
                      </button>
                    </div>
                  )}

                  {/* Assigned Teachers */}
                  {formAssignments[selectedForm]?.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-sm font-medium text-gray-700 mb-3">Assigned Teachers:</h3>
                      <div className="flex flex-wrap gap-2">
                        {formAssignments[selectedForm].map(email => (
                          <div key={email} className="flex items-center gap-2 bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
                            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                            {teachers.find(t => t.email === email)?.name || email}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Students Table */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="p-6 border-b border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-900">Students in Form {selectedForm}</h3>
                    {searchTerm && (
                      <p className="text-sm text-gray-600 mt-1">
                        Showing {filteredStudents.length} results for "{searchTerm}"
                      </p>
                    )}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Student
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            ID
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Gender
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Form
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Division
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredStudents.length > 0 ? (
                          filteredStudents.map((student, index) => (
                            <tr key={index} className="hover:bg-gray-50 transition-colors">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                    <span className="text-blue-700 font-medium text-sm">
                                      {student.full_name.split(' ').map(n => n[0]).join('')}
                                    </span>
                                  </div>
                                  <div>
                                    <div className="text-sm font-medium text-gray-900">{student.full_name}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {student.school_id}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  student.gender === 'M' 
                                    ? 'bg-blue-100 text-blue-800' 
                                    : 'bg-pink-100 text-pink-800'
                                }`}>
                                  {student.gender === 'M' ? 'Male' : 'Female'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {student.form}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {student.division_name}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <Link
                                  to={`/student/${student.school_id}`}
                                  className="text-blue-600 hover:text-blue-900 transition-colors"
                                >
                                  View Profile
                                </Link>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={6} className="px-6 py-12 text-center">
                              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <span className="text-2xl">👥</span>
                              </div>
                              <p className="text-gray-500 text-lg">
                                {searchTerm ? "No students found matching your search" : "No students in this form"}
                              </p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {navTab === "UserManagement" && (
          <div>
            <UserManagement />
          </div>
        )}
        
        {navTab === "Settings" && (
          <div>
            <div className="flex border-b mb-4">
              <button
                className={`px-4 py-2 ${settingsTab === "Classes" ? "border-b-2 border-blue-500 font-bold" : ""}`}
                onClick={() => setSettingsTab("Classes")}
              >
                Classes
              </button>
              <button
                className={`px-4 py-2 ${settingsTab === "Pupils" ? "border-b-2 border-blue-500 font-bold" : ""}`}
                onClick={() => setSettingsTab("Pupils")}
              >
                Pupils
              </button>
              <button
                className={`px-4 py-2 ${settingsTab === "Applicants" ? "border-b-2 border-blue-500 font-bold" : ""}`}
                onClick={() => setSettingsTab("Applicants")}
              >
                Applicants
              </button>
              <button
                className={`px-4 py-2 ${settingsTab === "Leavers" ? "border-b-2 border-blue-500 font-bold" : ""}`}
                onClick={() => setSettingsTab("Leavers")}
              >
                Leavers
              </button>
              <button
                className={`px-4 py-2 ${settingsTab === "Notifications" ? "border-b-2 border-blue-500 font-bold" : ""}`}
                onClick={() => setSettingsTab("Notifications")}
              >
                🔔 Notifications
              </button>
              <button
                className={`px-4 py-2 ${settingsTab === "Devices" ? "border-b-2 border-green-500 font-bold" : ""}`}
                onClick={() => setSettingsTab("Devices")}
              >
                🖥️ Devices
              </button>
            </div>

            {/* Classes Tab */}
            {settingsTab === "Classes" && (
              <div>
                <h2 className="text-xl font-semibold mb-2">Forms List</h2>
                <ul>
                  {forms.map((form) => (
                    <li key={form} className="border p-2 mb-2 rounded bg-white">{form}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Pupils Tab */}
            {settingsTab === "Pupils" && (
              <div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-xl font-semibold">All Pupils</span>
                  <button 
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                    onClick={handleSyncNow}
                  >
                    Sync with ISAMS
                  </button>
                </div>
                
                {/* Pupils Statistics */}
                <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {getTotalPupilsStats().total}
                      </div>
                      <div className="text-sm text-blue-600 font-medium">
                        Total Pupils
                      </div>
                    </div>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {getTotalPupilsStats().male}
                      </div>
                      <div className="text-sm text-green-600 font-medium">
                        Male Students
                      </div>
                    </div>
                  </div>
                  <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-pink-600">
                        {getTotalPupilsStats().female}
                      </div>
                      <div className="text-sm text-pink-600 font-medium">
                        Female Students
                      </div>
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto rounded shadow border">
                  <table className="min-w-full bg-white">
                    <thead>
                      <tr>
                        <th className="py-2 px-3 border-b text-left">Name</th>
                        <th className="py-2 px-3 border-b text-left">DOB</th>
                        <th className="py-2 px-3 border-b text-left">Form</th>
                        <th className="py-2 px-3 border-b text-left">School ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dedupedPupils.map((pupil) => (
                        <tr key={pupil.person_id}>
                          <td className="py-2 px-3 border-b">{pupil.full_name}</td>
                          <td className="py-2 px-3 border-b">{pupil.dob?.slice(0, 10)}</td>
                          <td className="py-2 px-3 border-b">{pupil.form}</td>
                          <td className="py-2 px-3 border-b">{pupil.school_id}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Applicants Tab */}
            {settingsTab === "Applicants" && (
              <div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-xl font-semibold">Applicants (Last 5 Years)</span>
                  <button 
                    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:bg-gray-400"
                    onClick={() => fetchApplicants(true)}
                    disabled={isLoadingApplicants}
                  >
                    {isLoadingApplicants ? 'Loading...' : 'Fetch & Save Applicants'}
                  </button>
                </div>
                
                {/* Applicants Statistics */}
                <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {applicantsData.length}
                      </div>
                      <div className="text-sm text-green-600 font-medium">
                        Total Applicants
                      </div>
                    </div>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {applicantsData.filter(a => a.school_name?.toLowerCase().includes('senior') || a.school_name?.toLowerCase().includes('main')).length}
                      </div>
                      <div className="text-sm text-blue-600 font-medium">
                        Main School
                      </div>
                    </div>
                  </div>
                  <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-pink-600">
                        {applicantsData.filter(a => a.school_name?.toLowerCase().includes('girls')).length}
                      </div>
                      <div className="text-sm text-pink-600 font-medium">
                        Girls School
                      </div>
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto rounded shadow border">
                  <table className="min-w-full bg-white">
                    <thead>
                      <tr>
                        <th className="py-2 px-3 border-b text-left">Name</th>
                        <th className="py-2 px-3 border-b text-left">DOB</th>
                        <th className="py-2 px-3 border-b text-left">School</th>
                        <th className="py-2 px-3 border-b text-left">Year Group</th>
                        <th className="py-2 px-3 border-b text-left">Application Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applicantsData.map((applicant, index) => (
                        <tr key={index}>
                          <td className="py-2 px-3 border-b">{applicant.full_name || applicant.name}</td>
                          <td className="py-2 px-3 border-b">{applicant.dob?.slice(0, 10) || 'N/A'}</td>
                          <td className="py-2 px-3 border-b">{applicant.school_name || 'N/A'}</td>
                          <td className="py-2 px-3 border-b">{applicant.year_group || 'N/A'}</td>
                          <td className="py-2 px-3 border-b">{applicant.application_date?.slice(0, 10) || 'N/A'}</td>
                        </tr>
                      ))}
                      {applicantsData.length === 0 && (
                        <tr>
                          <td colSpan="5" className="py-4 px-3 text-center text-gray-500">
                            No applicants data. Click "Fetch & Save Applicants" to load data.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Leavers Tab */}
            {settingsTab === "Leavers" && (
              <div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-xl font-semibold">All Leavers</span>
                  <button 
                    className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:bg-gray-400"
                    onClick={() => fetchLeavers(true)}
                    disabled={isLoadingLeavers}
                  >
                    {isLoadingLeavers ? 'Loading...' : 'Fetch & Save Leavers'}
                  </button>
                </div>
                
                {/* Leavers Statistics */}
                <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-red-600">
                        {leaversData.length}
                      </div>
                      <div className="text-sm text-red-600 font-medium">
                        Total Leavers
                      </div>
                    </div>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {leaversData.filter(l => l.school?.toLowerCase().includes('senior') || l.school?.toLowerCase().includes('main')).length}
                      </div>
                      <div className="text-sm text-blue-600 font-medium">
                        Main School
                      </div>
                    </div>
                  </div>
                  <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-pink-600">
                        {leaversData.filter(l => l.school?.toLowerCase().includes('girls')).length}
                      </div>
                      <div className="text-sm text-pink-600 font-medium">
                        Girls School
                      </div>
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto rounded shadow border">
                  <table className="min-w-full bg-white">
                    <thead>
                      <tr>
                        <th className="py-2 px-3 border-b text-left">Name</th>
                        <th className="py-2 px-3 border-b text-left">DOB</th>
                        <th className="py-2 px-3 border-b text-left">School</th>
                        <th className="py-2 px-3 border-b text-left">Year Group</th>
                        <th className="py-2 px-3 border-b text-left">Leaving Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaversData.map((leaver, index) => (
                        <tr key={index}>
                          <td className="py-2 px-3 border-b">{leaver.full_name || leaver.name}</td>
                          <td className="py-2 px-3 border-b">{leaver.dob?.slice(0, 10) || 'N/A'}</td>
                          <td className="py-2 px-3 border-b">{leaver.school || 'N/A'}</td>
                          <td className="py-2 px-3 border-b">{leaver.year_group || 'N/A'}</td>
                          <td className="py-2 px-3 border-b">{leaver.leaving_date?.slice(0, 10) || 'N/A'}</td>
                        </tr>
                      ))}
                      {leaversData.length === 0 && (
                        <tr>
                          <td colSpan="5" className="py-4 px-3 text-center text-gray-500">
                            No leavers data. Click "Fetch & Save Leavers" to load data.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Notifications Tab */}
            {settingsTab === "Notifications" && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h2 className="text-xl font-semibold">🔔 Module Notification Settings</h2>
                    <p className="text-gray-500 text-sm mt-1">Enable or disable notifications per module for parents and students. When enabled, new activity in that module will show an unread badge on their notification icon.</p>
                  </div>
                  {notifSaveStatus && (
                    <span className="text-sm font-medium text-green-600">{notifSaveStatus}</span>
                  )}
                </div>

                {isLoadingNotifications ? (
                  <div className="text-center py-8">
                    <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                    <p className="text-gray-500">Loading notification settings...</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-lg shadow border">
                    <table className="min-w-full bg-white">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="py-3 px-4 border-b text-left font-semibold text-gray-700">Module</th>
                          <th className="py-3 px-4 border-b text-center font-semibold text-gray-700">Parents</th>
                          <th className="py-3 px-4 border-b text-center font-semibold text-gray-700">Students</th>
                        </tr>
                      </thead>
                      <tbody>
                        {notificationSettings.map((setting) => (
                          <tr key={setting.module_id} className="hover:bg-gray-50 transition-colors">
                            <td className="py-3 px-4 border-b">
                              <div className="flex items-center gap-2">
                                <span className="text-lg">
                                  {setting.module_id === 'posts' ? '📢' :
                                   setting.module_id === 'assignments' ? '📝' :
                                   setting.module_id === 'consent-forms' ? '📋' :
                                   setting.module_id === 'club-booking' ? '⚽' :
                                   setting.module_id === 'parents-evening' ? '👨‍👩‍👧' :
                                   setting.module_id === 'resources' ? '📚' :
                                   setting.module_id === 'notes' ? '📓' :
                                   setting.module_id === 'timetable' ? '📅' :
                                   setting.module_id === 'school-policies' ? '📜' : '🔔'}
                                </span>
                                <span className="font-medium text-gray-800">{setting.module_label}</span>
                              </div>
                            </td>
                            <td className="py-3 px-4 border-b text-center">
                              <button
                                onClick={() => handleToggleNotification(setting.module_id, 'enabled_for_parents', !setting.enabled_for_parents)}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                                  setting.enabled_for_parents ? 'bg-green-500' : 'bg-gray-300'
                                }`}
                              >
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                                    setting.enabled_for_parents ? 'translate-x-6' : 'translate-x-1'
                                  }`}
                                />
                              </button>
                            </td>
                            <td className="py-3 px-4 border-b text-center">
                              <button
                                onClick={() => handleToggleNotification(setting.module_id, 'enabled_for_students', !setting.enabled_for_students)}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                                  setting.enabled_for_students ? 'bg-green-500' : 'bg-gray-300'
                                }`}
                              >
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                                    setting.enabled_for_students ? 'translate-x-6' : 'translate-x-1'
                                  }`}
                                />
                              </button>
                            </td>
                          </tr>
                        ))}
                        {notificationSettings.length === 0 && (
                          <tr>
                            <td colSpan="3" className="py-4 px-3 text-center text-gray-500">
                              No notification modules configured.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Devices Tab */}
            {settingsTab === "Devices" && <DevicesTab />}
          </div>
        )}

        {navTab === "Reports" && (
          <div>
            <div className="mb-8">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => onBack ? onBack() : setNavTab("Home")}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                    title="Back"
                  >
                    <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                  </button>
                  <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold">📊</span>
                  </div>
                  <h1 className="text-3xl font-bold text-gray-900">Reports</h1>
                </div>
              </div>
              <p className="text-gray-600">Generate and view school reports and analytics</p>
            </div>

            <div className="flex border-b mb-4">
              <button
                className={`px-4 py-2 ${activeReportTab === "pupils-overview" ? "border-b-2 border-purple-500 font-bold" : ""}`}
                onClick={() => setActiveReportTab("pupils-overview")}
              >
                Pupils Overview
              </button>
              <button
                className={`px-4 py-2 ${activeReportTab === "school-overview" ? "border-b-2 border-blue-500 font-bold" : ""}`}
                onClick={() => setActiveReportTab("school-overview")}
              >
                Pupil Attendance
              </button>
              <button
                className={`px-4 py-2 ${activeReportTab === "applicants" ? "border-b-2 border-green-500 font-bold" : ""}`}
                onClick={() => setActiveReportTab("applicants")}
              >
                Applicants
              </button>
              <button
                className={`px-4 py-2 ${activeReportTab === "leavers" ? "border-b-2 border-red-500 font-bold" : ""}`}
                onClick={() => setActiveReportTab("leavers")}
              >
                Leavers
              </button>
              <button
                className={`px-4 py-2 ${activeReportTab === "capacity" ? "border-b-2 border-orange-500 font-bold" : ""}`}
                onClick={() => setActiveReportTab("capacity")}
              >
                Capacity & Availability
              </button>
              <button
                className={`px-4 py-2 ${activeReportTab === "student-reports" ? "border-b-2 border-indigo-500 font-bold" : ""}`}
                onClick={() => setActiveReportTab("student-reports")}
              >
                Student Reports
              </button>
            </div>

            {/* Pupils Overview Tab */}
            {activeReportTab === "pupils-overview" && (
              <PupilsOverviewReport 
                students={students}
              />
            )}

            {/* Pupil Attendance Tab */}
            {activeReportTab === "school-overview" && (
              <SchoolOverviewReport 
                students={students}
                registrationData={registrationData}
                setRegistrationData={setRegistrationData}
                isLoadingRegistration={isLoadingRegistration}
                setIsLoadingRegistration={setIsLoadingRegistration}
              />
            )}

            {/* Applicants Tab */}
            {activeReportTab === "applicants" && (
              <ApplicantsReport 
                applicantsData={applicantsData}
                setApplicantsData={setApplicantsData}
                isLoadingApplicants={isLoadingApplicants}
                setIsLoadingApplicants={setIsLoadingApplicants}
                applicantsSchool={applicantsSchool}
                setApplicantsSchool={setApplicantsSchool}
                applicantsSchoolYear={applicantsSchoolYear}
                setApplicantsSchoolYear={setApplicantsSchoolYear}
                applicantsYear={applicantsYear}
                setApplicantsYear={setApplicantsYear}
                applicantsMonth={applicantsMonth}
                setApplicantsMonth={setApplicantsMonth}
                applicantsAdmissionStatus={applicantsAdmissionStatus}
                setApplicantsAdmissionStatus={setApplicantsAdmissionStatus}
                applicantsStartingYear={applicantsStartingYear}
                setApplicantsStartingYear={setApplicantsStartingYear}
                fetchApplicants={fetchApplicants}
                students={students}
              />
            )}

            {/* Leavers Tab */}
            {activeReportTab === "leavers" && (
              <LeaversReport 
                leaversData={leaversData}
                isLoadingLeavers={isLoadingLeavers}
                leaversSchool={leaversSchool}
                setLeaversSchool={setLeaversSchool}
                leaversYear={leaversYear}
                setLeaversYear={setLeaversYear}
                leaversMonth={leaversMonth}
                setLeaversMonth={setLeaversMonth}
                fetchLeavers={fetchLeavers}
              />
            )}

            {/* Capacity & Availability Tab */}
            {activeReportTab === "capacity" && (
              <CapacityReport 
                students={students}
                applicantsData={applicantsData}
                fetchApplicants={fetchApplicants}
                isLoadingApplicants={isLoadingApplicants}
              />
            )}

            {/* Student Reports Tab */}
            {activeReportTab === "student-reports" && (
              <StudentReportsManager students={students} />
            )}
          </div>
        )}

        {navTab === "Timetable" && (
          <AdminTimetableManager />
        )}

        {navTab === "ParentsEvening" && (
          <div>
            {/* Test Mode Toggle - Remove in production */}
            <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h3 className="text-sm font-medium text-yellow-800 mb-2">🧪 Test Mode - Switch User Role:</h3>
              <div className="flex gap-2">
                <button 
                  onClick={() => window.parentTestMode = false}
                  className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Admin Mode
                </button>
                <button 
                  onClick={() => window.parentTestMode = true}
                  className="px-3 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
                >
                  Parent Mode (m.kn321@yahoo.co.uk)
                </button>
              </div>
            </div>
            
            <ParentsEveningSystem 
              key={navTab} 
              userRole={window.parentTestMode ? "parent" : "admin"} 
              userEmail={window.parentTestMode ? "m.kn321@yahoo.co.uk" : "admin@school.com"} 
              userName={window.parentTestMode ? "Mazin Kamal Nasreldin Abdalla" : "Administrator"} 
            />
          </div>
        )}

        {navTab === "EmergencyAlerts" && (
          <EmergencyAlertsSimple />
        )}

        {navTab === "ClubBooking" && (
          <div>
            <ClubBookingManager />
          </div>
        )}

        {navTab === "QuickLinks" && (
          <QuickLinksManager />
        )}

        {navTab === "ConsentForms" && (
          <ConsentFormsManager />
        )}

        {navTab === "SchoolPolicies" && (
          <SchoolPoliciesManager />
        )}

        {navTab === "ParentsManager" && (
          <ParentsManager />
        )}
      </div>
    </div>
  );
};
